import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Select from "react-select";
import Swal from "sweetalert2";
import apiurl from "../Api";
import RefreshContext from "./RefreshContext";
import "./css/advancePayment.css";

const sections = [
  { key: "apply", label: "Apply for Advance", icon: "ri-hand-coin-line", emptyIcon: "ri-file-list-3-line" },
  { key: "expenses", label: "Expenses Tracking", icon: "ri-line-chart-line", emptyIcon: "ri-receipt-line" },
  { key: "recovery", label: "Recovery", icon: "ri-refund-2-line", emptyIcon: "ri-loop-left-line" },
];

const advanceReasons = [
  "Travel",
  "Medical test",
  "Police verification",
  "Local Travel",
  "Xerox printout",
  "Emergency expenses",
  "Other",
];

const initialForm = {
  reasons: [],
  otherCause: "",
  clientName: "",
  location: "",
  amount: "",
};

const initialDecisionModal = {
  open: false,
  action: "approve",
  request: null,
  remark: "",
  error: "",
};

const initialSpocModal = {
  open: false,
  request: null,
  form: {
    spocName: "",
    spocMobile: "",
    spocEmail: "",
    amountTransferred: "",
    transferredDate: "",
    transferCause: "",
  },
  errors: {},
};

const initialSpocViewModal = {
  open: false,
  request: null,
};

const generateExpenseEntryId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `exp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const createExpenseEntry = (seed = {}) => ({
  entryId: seed.entry_id || generateExpenseEntryId(),
  spentOn: seed.spent_on || "candidate",
  purpose: seed.purpose || "",
  expenseDate: seed.expense_date || "",
  candidateName: seed.candidate_name || "",
  mobileNumber: seed.mobile_number || "",
  aadhaarNumber: seed.aadhaar_number || "",
  amountSpent: seed.amount_spent ? String(seed.amount_spent) : "",
  reason: seed.reason || "",
  documents: Array.isArray(seed.documents) ? seed.documents : [],
});

const spentOnOptions = [
  { value: "candidate", label: "Candidate" },
  { value: "self", label: "Self" },
];

const initialExpensesModal = {
  open: false,
  request: null,
  entries: [],
  errors: [],
  page: 1,
  loading: false,
};

const initialExpensesViewModal = {
  open: false,
  request: null,
  entries: [],
  page: 1,
  totalEntries: 0,
  totalPages: 1,
  loading: false,
};

const initialExpensesUploadModal = {
  open: false,
  request: null,
  file: null,
  fileName: "",
  errors: [],
  generalError: "",
};

const initialDocumentsModal = {
  open: false,
  request: null,
  documents: [],
  canUpload: false,
  uploading: false,
  error: "",
};

const EXPENSES_PAGE_SIZE = 10;
const MAIN_TABLE_PAGE_SIZE = 20;

const initialBankModal = {
  open: false,
  form: { holderName: "", accountNo: "", confirmAccountNo: "", ifsc: "" },
  errors: {},
  submitting: false,
};

const initialAccountsModal = {
  open: false,
  request: null,
  utrNo: "",
  remark: "",
  markPaid: false,
};

const initialRecoveryModal = {
  open: false,
  request: null,
  form: {
    estimatedRecoveryAmount: "",
    estimatedRecoveryDate: "",
    actualRecoveryAmount: "",
    actualRecoveryDate: "",
    nonRecoveryAmount: "",
    nonRecoveryReason: "",
    recoveryRemark: "",
  },
  errors: {},
};

const initialRecoveryViewModal = {
  open: false,
  request: null,
};

const initialMarkRecoveredModal = {
  open: false,
  request: null,
  entries: [],
  entriesById: {},
  values: {},
  touched: {},
  rowErrors: {},
  remark: "",
  page: 1,
  totalEntries: 0,
  totalPages: 1,
  loading: false,
  submitting: false,
  error: "",
};

const stageLabels = {
  sourcing_manager: "SM Approval",
  sourcing_head: "SH Approval",
  cxo: "CXO Approval",
  business_manager_cxo: "CXO Approval",
  accounts: "Accounts Processing",
  completed: "Completed",
};

const statusStageMeta = {
  sourcing_manager: { title: "Sourcing Manager", prefix: "sm" },
  sourcing_head: { title: "Sourcing Head", prefix: "sh" },
  business_manager: { title: "Business Manager", prefix: "bm" },
  cxo: { title: "CXO", prefix: "cxo" },
  accounts: { title: "Accounts", prefix: null },
};

const initialStatusModal = { open: false, request: null };

const buildStatusSteps = (row) => {
  if (!row) return [];
  const steps = [];

  steps.push({
    title: "Request Created",
    icon: "done",
    actorName: row.created_by_name || "-",
    actorPost: (row.created_by_post || "").replace(/_/g, " ") || "-",
    actorAt: row.created_at,
  });

  const timeline = Array.isArray(row.timeline) ? row.timeline : [];
  timeline.forEach((entry) => {
    const meta = statusStageMeta[entry.key] || { title: entry.label || entry.key, prefix: null };
    const state = entry.state || "waiting";
    const step = { title: meta.title, state };

    if (entry.key === "accounts") {
      const actor = row.accounts_action_by_name || "";
      const at = row.accounts_action_at || "";
      if (state === "paid") {
        step.icon = "done";
        step.actorName = actor || "-";
        step.actorPost = "Accounts";
        step.actorAt = row.payment_marked_at || at;
        if (row.utr_no) step.extra = `UTR: ${row.utr_no}`;
      } else if (state === "utr_added") {
        step.icon = "progress";
        step.actorName = actor || "-";
        step.actorPost = "Accounts";
        step.actorAt = at;
        if (row.utr_no) step.extra = `UTR: ${row.utr_no}`;
        step.pendingNote = "Awaiting payment marking";
      } else if (state === "pending") {
        step.icon = "pending";
        step.pendingWith = "Accounts Team";
      } else if (state === "rejected") {
        step.icon = "rejected";
      } else {
        step.icon = "waiting";
      }
    } else {
      const prefix = meta.prefix;
      const decision = prefix ? String(row[`${prefix}_decision`] || "").toLowerCase() : "";
      const actor = prefix ? (row[`${prefix}_action_by_name`] || "") : "";
      const at = prefix ? (row[`${prefix}_action_at`] || "") : "";
      if (decision === "approved") {
        step.icon = "done";
        step.actorName = actor || "-";
        step.actorPost = meta.title;
        step.actorAt = at;
        if (row[`${prefix}_remark`]) step.extra = row[`${prefix}_remark`];
      } else if (decision === "rejected") {
        step.icon = "rejected";
        step.actorName = actor || "-";
        step.actorPost = meta.title;
        step.actorAt = at;
        if (row[`${prefix}_remark`]) step.extra = row[`${prefix}_remark`];
      } else if (state === "pending") {
        step.icon = "pending";
        step.pendingWith = row[`${entry.key}_name`] || meta.title;
      } else {
        step.icon = "waiting";
      }
    }

    steps.push(step);
  });

  return steps;
};

const parseAmountInput = (value) => String(value || "").replace(/,/g, "").trim();

const formatAmountInput = (value) => {
  const cleaned = parseAmountInput(value).replace(/[^\d.]/g, "");
  if (!cleaned) return "";

  const parts = cleaned.split(".");
  const integerPart = parts[0] || "";
  const decimalPart = parts.slice(1).join("").slice(0, 2);
  const formattedInteger = integerPart ? Number(integerPart).toLocaleString("en-IN") : "";

  if (cleaned.endsWith(".") && parts.length === 2) {
    return `${formattedInteger}.`;
  }

  return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};

const validateSpocForm = (formData) => {
  const errors = {};
  const normalizedAmount = parseAmountInput(formData.amountTransferred);
  const mobileDigits = String(formData.spocMobile || "").replace(/\D/g, "");

  if (!formData.spocName.trim()) {
    errors.spocName = "SPOC name is required.";
  }

  if (!formData.spocMobile.trim()) {
    errors.spocMobile = "Mobile number is required.";
  } else if (mobileDigits.length !== 10) {
    errors.spocMobile = "Mobile number must be 10 digits.";
  }

  if (!formData.spocEmail.trim()) {
    errors.spocEmail = "Email ID is required.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.spocEmail.trim())) {
    errors.spocEmail = "Enter a valid email ID.";
  }

  if (!normalizedAmount) {
    errors.amountTransferred = "Transferred amount is required.";
  } else if (Number(normalizedAmount) <= 0) {
    errors.amountTransferred = "Enter a valid transferred amount.";
  }

  if (!formData.transferredDate) {
    errors.transferredDate = "Transfer date is required.";
  }

  if (!formData.transferCause.trim()) {
    errors.transferCause = "Cause for transfer is required.";
  }

  return errors;
};

const sanitizeMobileInput = (value) => String(value || "").replace(/\D/g, "").slice(0, 10);
const sanitizeAadhaarInput = (value) => String(value || "").replace(/\D/g, "").slice(0, 12);

const validateExpenseEntries = (entries) =>
  entries.map((entry) => {
    const rowErrors = {};
    const amountSpent = parseAmountInput(entry.amountSpent);
    const mobileDigits = String(entry.mobileNumber || "").replace(/\D/g, "");
    const aadhaarDigits = String(entry.aadhaarNumber || "").replace(/\D/g, "");

    if (!entry.purpose.trim()) rowErrors.purpose = "Purpose is required.";
    if (!entry.expenseDate) rowErrors.expenseDate = "Expense date is required.";
    if (!entry.candidateName.trim()) rowErrors.candidateName = "Candidate name is required.";
    if (!mobileDigits) {
      rowErrors.mobileNumber = "Mobile number is required.";
    } else if (mobileDigits.length !== 10) {
      rowErrors.mobileNumber = "Mobile number must be 10 digits.";
    }
    if (!aadhaarDigits) {
      rowErrors.aadhaarNumber = "Aadhaar number is required.";
    } else if (aadhaarDigits.length !== 12) {
      rowErrors.aadhaarNumber = "Aadhaar number must be 12 digits.";
    }
    if (!amountSpent) {
      rowErrors.amountSpent = "Spent amount is required.";
    } else if (Number(amountSpent) <= 0) {
      rowErrors.amountSpent = "Enter a valid spent amount.";
    }
    if (!entry.reason.trim()) rowErrors.reason = "Reason is required.";

    return rowErrors;
  });

const validateAdvanceForm = (formData) => {
  const errors = {};
  const normalizedAmount = parseAmountInput(formData.amount);

  if (formData.reasons.length === 0) {
    errors.reasons = "Please select at least one purpose.";
  }

  if (formData.reasons.includes("Other") && !formData.otherCause.trim()) {
    errors.otherCause = "Please enter the cause.";
  }

  if (!formData.clientName.trim()) {
    errors.clientName = "Client name is required.";
  }

  if (!formData.location.trim()) {
    errors.location = "Location is required.";
  }

  if (!normalizedAmount) {
    errors.amount = "Amount is required.";
  } else if (Number(normalizedAmount) <= 0) {
    errors.amount = "Enter a valid amount.";
  }

  return errors;
};

const validateRecoveryForm = (formData, remainingRecoveryCapacity) => {
  const errors = {};
  const estimatedAmount = parseAmountInput(formData.estimatedRecoveryAmount);
  const actualAmount = parseAmountInput(formData.actualRecoveryAmount);
  const nonRecoveryAmount = parseAmountInput(formData.nonRecoveryAmount);
  const estimatedValue = estimatedAmount ? Number(estimatedAmount) : 0;
  const actualValue = actualAmount ? Number(actualAmount) : 0;
  const nonRecoveryValue = nonRecoveryAmount ? Number(nonRecoveryAmount) : 0;
  const capacityValue = Number(remainingRecoveryCapacity || 0);

  if (estimatedAmount && estimatedValue <= 0) {
    errors.estimatedRecoveryAmount = "Enter a valid estimated recovery amount.";
  }

  if (actualAmount && actualValue <= 0) {
    errors.actualRecoveryAmount = "Enter a valid actual recovery amount.";
  }

  if (nonRecoveryAmount && nonRecoveryValue <= 0) {
    errors.nonRecoveryAmount = "Enter a valid non recovery amount.";
  }

  if ((estimatedValue + actualValue + nonRecoveryValue) > capacityValue) {
    const capacityMessage = "This entry exceeds the remaining recovery capacity.";
    if (nonRecoveryValue > 0) {
      errors.nonRecoveryAmount = capacityMessage;
    } else if (actualValue > 0) {
      errors.actualRecoveryAmount = capacityMessage;
    } else if (estimatedValue > 0) {
      errors.estimatedRecoveryAmount = capacityMessage;
    } else {
      errors.form = capacityMessage;
    }
  }

  if (estimatedValue > 0 && !formData.estimatedRecoveryDate) {
    errors.estimatedRecoveryDate = "Estimated recovery date is required.";
  }

  if (actualValue > 0 && !formData.actualRecoveryDate) {
    errors.actualRecoveryDate = "Actual recovery date is required.";
  }

  if (nonRecoveryValue > 0 && !formData.nonRecoveryReason.trim()) {
    errors.nonRecoveryReason = "Non recovery reason is required.";
  }

  if (
    !estimatedAmount &&
    !actualAmount &&
    !nonRecoveryAmount &&
    !formData.estimatedRecoveryDate &&
    !formData.actualRecoveryDate &&
    !formData.nonRecoveryReason.trim()
  ) {
    errors.form = "Please add at least one recovery amount.";
  }

  return errors;
};

const formatAmount = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return `INR ${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatFileSize = (bytes) => {
  const num = Number(bytes);
  if (!Number.isFinite(num) || num <= 0) return "-";
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
  return `${(num / (1024 * 1024)).toFixed(1)} MB`;
};

const isImageDocument = (doc) => {
  const mime = String(doc?.mime_type || "").toLowerCase();
  const name = String(doc?.original_name || "").toLowerCase();
  return mime.startsWith("image/") || /\.(jpg|jpeg|png|webp)$/.test(name);
};

const isPdfDocument = (doc) => {
  const mime = String(doc?.mime_type || "").toLowerCase();
  const name = String(doc?.original_name || "").toLowerCase();
  return mime === "application/pdf" || name.endsWith(".pdf");
};

const isOfficeDocument = (doc) => {
  const mime = String(doc?.mime_type || "").toLowerCase();
  const name = String(doc?.original_name || "").toLowerCase();
  return /\.(doc|docx)$/.test(name) || mime.includes("word");
};

const isPreviewableDocument = (doc) => isImageDocument(doc) || isPdfDocument(doc) || isOfficeDocument(doc);

const getDocIconType = (doc) => {
  if (isImageDocument(doc)) return "image";
  if (isPdfDocument(doc)) return "pdf";
  if (isOfficeDocument(doc)) return "doc";
  return "other";
};

const buildDocPreviewUrl = (doc) => {
  if (isOfficeDocument(doc)) {
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(doc.url)}`;
  }
  return doc.url;
};

const getDocIcon = (type) => {
  if (type === "pdf") return "ri-file-pdf-2-line";
  if (type === "doc") return "ri-file-word-2-line";
  if (type === "image") return "ri-image-2-line";
  return "ri-file-3-line";
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateOnly = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const normalizeStateLabel = (state) => {
  if (!state) return "Waiting";
  if (state === "utr_added") return "UTR Added";
  return state.charAt(0).toUpperCase() + state.slice(1).replace(/_/g, " ");
};

function AdvancePayment() {
  const { refreshKey, triggerRefresh } = useContext(RefreshContext);
  const [activeSection, setActiveSection] = useState("apply");
  const [loading, setLoading] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [sessionUser, setSessionUser] = useState(null);
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [clientOptions, setClientOptions] = useState([]);
  const [clientOptionsLoading, setClientOptionsLoading] = useState(false);
  const [busyByRequest, setBusyByRequest] = useState({});
  const [accountsDraftByRequest, setAccountsDraftByRequest] = useState({});
  const [decisionModal, setDecisionModal] = useState(initialDecisionModal);
  const [spocModal, setSpocModal] = useState(initialSpocModal);
  const [spocViewModal, setSpocViewModal] = useState(initialSpocViewModal);
  const [expensesModal, setExpensesModal] = useState(initialExpensesModal);
  const [expensesViewModal, setExpensesViewModal] = useState(initialExpensesViewModal);
  const [expensesUploadModal, setExpensesUploadModal] = useState(initialExpensesUploadModal);
  const [documentsModal, setDocumentsModal] = useState(initialDocumentsModal);
  const [docDragOver, setDocDragOver] = useState(false);
  const [docPreview, setDocPreview] = useState(null);
  const [accountsModal, setAccountsModal] = useState(initialAccountsModal);
  const [bankDetails, setBankDetails] = useState(null);
  const [bankModal, setBankModal] = useState(initialBankModal);
  const [copiedKey, setCopiedKey] = useState("");
  const [applySearchInput, setApplySearchInput] = useState("");
  const [appliedApplySearch, setAppliedApplySearch] = useState("");
  const [requesterFilter, setRequesterFilter] = useState([]);
  const [requesterOptions, setRequesterOptions] = useState([]);
  const [expensesSearchInput, setExpensesSearchInput] = useState("");
  const [appliedExpensesSearch, setAppliedExpensesSearch] = useState("");
  const [expensesRequesterFilter, setExpensesRequesterFilter] = useState([]);
  const [recoverySearchInput, setRecoverySearchInput] = useState("");
  const [appliedRecoverySearch, setAppliedRecoverySearch] = useState("");
  const [downloadModal, setDownloadModal] = useState({
    open: false,
    section: "expenses",
    requesterMode: "all",
    requesterIds: [],
    fromDate: "",
    toDate: "",
    submitting: false,
  });
  const [reusableSources, setReusableSources] = useState([]);
  const [reusableLoading, setReusableLoading] = useState(false);
  const [reuseEnabled, setReuseEnabled] = useState(false);
  const [reuseSource, setReuseSource] = useState(null);
  const [reuseAmountInput, setReuseAmountInput] = useState("");

  const copyToClipboard = (text, key) => {
    if (!text) return;
    try {
      navigator.clipboard?.writeText(text);
    } catch (e) {}
    setCopiedKey(key);
    setTimeout(() => setCopiedKey((prev) => (prev === key ? "" : prev)), 1400);
  };
  const [recoveryModal, setRecoveryModal] = useState(initialRecoveryModal);
  const [markRecoveredModal, setMarkRecoveredModal] = useState(initialMarkRecoveredModal);
  const [recoveryViewModal, setRecoveryViewModal] = useState(initialRecoveryViewModal);
  const [statusModal, setStatusModal] = useState(initialStatusModal);
  const [recoveryFilters, setRecoveryFilters] = useState({
    clientName: "",
    location: "",
  });
  const [recoveryFilterOptions, setRecoveryFilterOptions] = useState({
    clientOptions: [],
    locationOptions: [],
  });
  const [sectionPages, setSectionPages] = useState({
    apply: 1,
    expenses: 1,
    recovery: 1,
  });
  const [mainPagination, setMainPagination] = useState({
    section: "apply",
    page: 1,
    limit: MAIN_TABLE_PAGE_SIZE,
    totalItems: 0,
    totalPages: 1,
  });

  const activeMeta = useMemo(
    () => sections.find((section) => section.key === activeSection) || sections[0],
    [activeSection]
  );
  const isOtherSelected = form.reasons.includes("Other");
  const canApply = Boolean(sessionUser?.post && ["recruiter", "sourcing_manager", "sourcing_head"].includes(sessionUser.post));
  const recoveryClientSelectOptions = useMemo(
    () => recoveryFilterOptions.clientOptions.map((clientName) => ({ value: clientName, label: clientName })),
    [recoveryFilterOptions.clientOptions]
  );
  const recoveryLocationSelectOptions = useMemo(
    () => recoveryFilterOptions.locationOptions.map((locationName) => ({ value: locationName, label: locationName })),
    [recoveryFilterOptions.locationOptions]
  );
  const selectMenuPortalTarget = typeof document !== "undefined" ? document.body : null;
  const selectMenuPortalStyles = useMemo(
    () => ({
      menuPortal: (base) => ({ ...base, zIndex: 11050 }),
      menu: (base) => ({ ...base, zIndex: 11050 }),
    }),
    []
  );
  const advanceClientSelectOptions = useMemo(
    () =>
      clientOptions.map((client) => ({
        value: client.clientName,
        label: client.clientName,
        clientName: client.clientName,
        clientCode: client.clientCode,
        location: client.location,
      })),
    [clientOptions]
  );
  const selectedAdvanceClient =
    advanceClientSelectOptions.find((option) => option.clientName === form.clientName) || null;

  const fetchAdvanceClients = useCallback(async () => {
    try {
      setClientOptionsLoading(true);
      const response = await axios.get(`${apiurl}advance_payment.php?list=clients`);
      if (response.data?.status === true) {
        const rows = Array.isArray(response.data.data) ? response.data.data : [];
        const seen = new Set();
        const externalClients = rows
          .filter((row) => (row?.source || "").toString().trim() === "external_api")
          .map((row) => {
            const clientName = (row.client_name || "").toString().trim();
            const clientCode = (row.client_code || "").toString().trim();
            const uniqueKey = (clientCode || clientName).toLowerCase();
            if (!clientName || !uniqueKey || seen.has(uniqueKey)) return null;
            seen.add(uniqueKey);
            return {
              clientName,
              clientCode,
              location: (row.location || "").toString().trim(),
            };
          })
          .filter(Boolean);
        setClientOptions(externalClients);
      } else {
        setClientOptions([]);
      }
    } catch (error) {
      setClientOptions([]);
    } finally {
      setClientOptionsLoading(false);
    }
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const currentPage = sectionPages[activeSection] || 1;
      const response = await axios.get(`${apiurl}advance_payment.php`, {
        params: {
          section: activeSection,
          page: currentPage,
          limit: MAIN_TABLE_PAGE_SIZE,
          ...(activeSection === "recovery"
            ? {
                client_name_filter: recoveryFilters.clientName,
                location_filter: recoveryFilters.location,
                search: appliedRecoverySearch,
              }
            : {}),
          ...(activeSection === "apply"
            ? {
                search: appliedApplySearch,
                requester_filter: requesterFilter.join(","),
              }
            : {}),
          ...(activeSection === "expenses"
            ? {
                search: appliedExpensesSearch,
                requester_filter: expensesRequesterFilter.join(","),
              }
            : {}),
        },
      });
      if (response.data?.status === true) {
        const rows = response.data.data || [];
        setSessionUser(response.data.session_user || null);
        setBankDetails(response.data.bank_details || null);
        setRequests(rows);
        setRecoveryFilterOptions({
          clientOptions: Array.isArray(response.data.filters?.client_options) ? response.data.filters.client_options : [],
          locationOptions: Array.isArray(response.data.filters?.location_options) ? response.data.filters.location_options : [],
        });
        setRequesterOptions(Array.isArray(response.data.filters?.requester_options) ? response.data.filters.requester_options : []);
        setMainPagination({
          section: response.data.pagination?.section || activeSection,
          page: Number(response.data.pagination?.page || currentPage),
          limit: Number(response.data.pagination?.limit || MAIN_TABLE_PAGE_SIZE),
          totalItems: Number(response.data.pagination?.total_items || 0),
          totalPages: Number(response.data.pagination?.total_pages || 1),
        });

        const nextDrafts = {};
        rows.forEach((item) => {
          nextDrafts[item.id] = {
            utr_no: item.utr_no || "",
            remark: item.accounts_remark || "",
          };
        });
        setAccountsDraftByRequest(nextDrafts);
      } else {
        setRequests([]);
        if (activeSection === "recovery") {
          setRecoveryFilterOptions({
            clientOptions: [],
            locationOptions: [],
          });
        }
        setMainPagination((prev) => ({
          ...prev,
          section: activeSection,
          page: 1,
          totalItems: 0,
          totalPages: 1,
        }));
      }
    } catch (error) {
      setRequests([]);
      if (activeSection === "recovery") {
        setRecoveryFilterOptions({
          clientOptions: [],
          locationOptions: [],
        });
      }
      setMainPagination((prev) => ({
        ...prev,
        section: activeSection,
        page: 1,
        totalItems: 0,
        totalPages: 1,
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [refreshKey, activeSection, sectionPages, recoveryFilters, appliedApplySearch, requesterFilter, appliedExpensesSearch, expensesRequesterFilter, appliedRecoverySearch]);

  useEffect(() => {
    if (!showApplyModal) return;
    fetchAdvanceClients();
    (async () => {
      try {
        setReusableLoading(true);
        const resp = await axios.get(`${apiurl}advance_payment.php`, { params: { fetch: "reusable_sources" } });
        setReusableSources(resp.data?.status ? (resp.data.data || []) : []);
      } catch (e) {
        setReusableSources([]);
      } finally {
        setReusableLoading(false);
      }
    })();
  }, [showApplyModal, fetchAdvanceClients]);

  const resetAndCloseModal = () => {
    setShowApplyModal(false);
    setForm(initialForm);
    setErrors({});
    setReuseEnabled(false);
    setReuseSource(null);
    setReuseAmountInput("");
  };

  const setRequestBusy = (requestId, state) => {
    setBusyByRequest((prev) => ({ ...prev, [requestId]: state }));
  };

  const handleRecoveryFilterChange = (fieldName, option) => {
    const nextValue = option?.value || "";
    setRecoveryFilters((prev) => ({
      ...prev,
      [fieldName]: nextValue,
    }));
    setSectionPages((prev) => ({
      ...prev,
      recovery: 1,
    }));
  };

  const resetRecoveryFilters = () => {
    setRecoveryFilters({
      clientName: "",
      location: "",
    });
    setSectionPages((prev) => ({
      ...prev,
      recovery: 1,
    }));
  };

  const handleReasonToggle = (reason) => {
    setForm((prev) => {
      const selected = prev.reasons.includes(reason);
      const reasons = selected
        ? prev.reasons.filter((item) => item !== reason)
        : [...prev.reasons, reason];

      return {
        ...prev,
        reasons,
        otherCause: reasons.includes("Other") ? prev.otherCause : "",
      };
    });

    setErrors((prev) => {
      const next = { ...prev };
      delete next.reasons;
      if (reason === "Other") delete next.otherCause;
      return next;
    });
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    const nextValue = name === "amount" ? formatAmountInput(value) : value;
    setForm((prev) => ({ ...prev, [name]: nextValue }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleClientChange = (option) => {
    setForm((prev) => ({
      ...prev,
      clientName: option?.clientName || "",
      location: option ? (option.location || prev.location || "") : "",
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.clientName;
      if (option?.location) delete next.location;
      return next;
    });
  };

  const handleSubmitRequest = async (event) => {
    event.preventDefault();
    const validationErrors = validateAdvanceForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (!bankDetails || !bankDetails.account_no || !bankDetails.ifsc || !bankDetails.verified_at) {
      await Swal.fire(
        "Bank details required",
        "Please add and verify your bank details before applying for an advance.",
        "warning"
      );
      openBankModal();
      return;
    }

    const requestedAmt = Number(parseAmountInput(form.amount)) || 0;
    let reuseAmtNumber = 0;
    if (reuseEnabled) {
      if (!reuseSource) {
        await Swal.fire("Source missing", "Please select a previous request to reuse from.", "warning");
        return;
      }
      reuseAmtNumber = Number(parseAmountInput(reuseAmountInput)) || 0;
      if (!(reuseAmtNumber > 0)) {
        await Swal.fire("Reuse amount required", "Enter an amount greater than 0 to reuse.", "warning");
        return;
      }
      const sourceAvail = Number(reuseSource.available_raw || reuseSource.available || 0);
      if (reuseAmtNumber > sourceAvail + 0.0001) {
        await Swal.fire("Exceeds leftover", `Available leftover is ₹${sourceAvail.toFixed(2)}.`, "warning");
        return;
      }
      if (reuseAmtNumber > requestedAmt + 0.0001) {
        await Swal.fire("Exceeds request", "Reuse amount cannot exceed the requested amount.", "warning");
        return;
      }
    }

    const confirmAmount = formatAmount(requestedAmt);
    const freshAsk = Math.max(0, requestedAmt - reuseAmtNumber);
    const confirmHtml = reuseEnabled && reuseAmtNumber > 0 && reuseSource
      ? `
        <div style="text-align:left;font-size:14px;line-height:1.9;">
          <div style="display:flex;justify-content:space-between;">
            <span>Requested Amount:</span>
            <strong>${confirmAmount}</strong>
          </div>
          <div style="display:flex;justify-content:space-between;color:#15803d;">
            <span>Reused from ${reuseSource.request_code}:</span>
            <strong>− ${formatAmount(reuseAmtNumber)}</strong>
          </div>
          <hr style="margin:6px 0;" />
          <div style="display:flex;justify-content:space-between;font-size:15px;">
            <span><strong>Fresh Ask (Accounts):</strong></span>
            <strong style="color:#0e479c;">${formatAmount(freshAsk)}</strong>
          </div>
          ${freshAsk <= 0 ? `<div style="margin-top:8px;background:#fef3c7;border:1px solid #fde68a;padding:6px 8px;border-radius:6px;color:#92400e;font-size:12px;">Fully covered by reuse — no fresh transfer needed.</div>` : ""}
        </div>
      `
      : `You are about to raise an advance request of <strong>${confirmAmount}</strong>.`;

    const confirmResult = await Swal.fire({
      title: "Are you sure?",
      html: confirmHtml,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, create",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });
    if (!confirmResult.isConfirmed) {
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      form.reasons.forEach((reason) => formData.append("reasons[]", reason));
      formData.append("action", "create");
      formData.append("other_cause", form.otherCause.trim());
      formData.append("client_name", form.clientName.trim());
      formData.append("location", form.location.trim());
      formData.append("amount", parseAmountInput(form.amount));
      if (reuseEnabled && reuseSource && reuseAmtNumber > 0) {
        formData.append("reuse_from_request_id", String(reuseSource.id));
        formData.append("reuse_amount", String(reuseAmtNumber));
      }
      const response = await axios.post(`${apiurl}advance_payment.php`, formData);
      if (response.data?.status === true) {
        resetAndCloseModal();
        await fetchData();
        triggerRefresh();
        await Swal.fire("Saved", response.data.message || "Advance request submitted.", "success");
      } else {
        await Swal.fire("Error", response.data?.message || "Server Error", "error");
      }
    } catch (error) {
      await Swal.fire("Error", "Server Error", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const openBankModal = () => {
    const rawAcct = bankDetails?.account_no || "";
    const spaced = rawAcct ? rawAcct.replace(/(.{4})/g, "$1 ").trim() : "";
    setBankModal({
      open: true,
      form: {
        holderName: bankDetails?.holder_name || "",
        accountNo: spaced,
        confirmAccountNo: spaced,
        ifsc: bankDetails?.ifsc || "",
      },
      errors: {},
      submitting: false,
    });
  };

  const closeBankModal = () => {
    setBankModal(initialBankModal);
  };

  const formatAccountInput = (raw) => {
    const digits = (raw || "").replace(/\D/g, "").slice(0, 20);
    return digits.replace(/(.{4})/g, "$1 ").trim();
  };

  const handleBankInputChange = (event) => {
    const { name, value } = event.target;
    let nextValue;
    if (name === "ifsc") {
      nextValue = value.toUpperCase().replace(/\s+/g, "").slice(0, 11);
    } else if (name === "accountNo" || name === "confirmAccountNo") {
      nextValue = formatAccountInput(value);
    } else {
      nextValue = value.replace(/\s+/g, "");
    }
    setBankModal((prev) => ({
      ...prev,
      form: { ...prev.form, [name]: nextValue },
      errors: { ...prev.errors, [name]: "" },
    }));
  };

  const handleBankSubmit = async (event) => {
    event.preventDefault();
    const { holderName, accountNo, confirmAccountNo, ifsc } = bankModal.form;
    const accountDigits = (accountNo || "").replace(/\D/g, "");
    const confirmDigits = (confirmAccountNo || "").replace(/\D/g, "");
    const errors = {};
    if (!holderName.trim()) errors.holderName = "Holder name is required.";
    if (!accountDigits) errors.accountNo = "Account number is required.";
    else if (!/^[0-9]{6,20}$/.test(accountDigits)) errors.accountNo = "Account number must be 6-20 digits.";
    if (accountDigits !== confirmDigits) errors.confirmAccountNo = "Account numbers do not match.";
    if (!ifsc) errors.ifsc = "IFSC is required.";
    else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) errors.ifsc = "Invalid IFSC format.";

    if (Object.keys(errors).length > 0) {
      setBankModal((prev) => ({ ...prev, errors }));
      return;
    }

    try {
      setBankModal((prev) => ({ ...prev, submitting: true }));
      const formData = new FormData();
      formData.append("action", "update_bank_details");
      formData.append("holder_name", holderName.trim());
      formData.append("account_no", accountDigits);
      formData.append("ifsc", ifsc);
      const response = await axios.post(`${apiurl}advance_payment.php`, formData);
      const onTop = {
        customClass: { container: "ap-swal-on-top" },
        didOpen: () => {
          const c = document.querySelector(".ap-swal-on-top");
          if (c) c.style.zIndex = "999999";
        },
      };
      if (response.data?.status === true) {
        setBankDetails(response.data.data || null);
        closeBankModal();
        await Swal.fire({ title: "Saved", text: response.data.message || "Bank details verified.", icon: "success" });
      } else {
        await Swal.fire({
          title: "Verification failed",
          text: response.data?.message || "Could not verify bank details.",
          icon: "error",
          ...onTop,
        });
        setBankModal((prev) => ({ ...prev, submitting: false }));
      }
    } catch (error) {
      await Swal.fire({
        title: "Error",
        text: "Server Error",
        icon: "error",
        customClass: { container: "ap-swal-on-top" },
        didOpen: () => {
          const c = document.querySelector(".ap-swal-on-top");
          if (c) c.style.zIndex = "999999";
        },
      });
      setBankModal((prev) => ({ ...prev, submitting: false }));
    }
  };

  const openDecisionModal = (requestRow, action) => {
    setDecisionModal({
      open: true,
      action,
      request: requestRow,
      remark: "",
      error: "",
    });
  };

  const closeDecisionModal = () => {
    setDecisionModal(initialDecisionModal);
  };

  const openSpocModal = (requestRow) => {
    setSpocModal({
      open: true,
      request: requestRow,
      form: {
        spocName: requestRow.spoc_name || "",
        spocMobile: requestRow.spoc_mobile || "",
        spocEmail: requestRow.spoc_email || "",
        amountTransferred: Number(requestRow.spoc_amount_transferred || 0) > 0
          ? formatAmountInput(requestRow.spoc_amount_transferred)
          : "",
        transferredDate: requestRow.spoc_transferred_date || "",
        transferCause: requestRow.spoc_transfer_cause || "",
      },
      errors: {},
    });
  };

  const closeSpocModal = () => {
    setSpocModal(initialSpocModal);
  };

  const openSpocViewModal = (requestRow) => {
    setSpocViewModal({
      open: true,
      request: requestRow,
    });
  };

  const closeSpocViewModal = () => {
    setSpocViewModal(initialSpocViewModal);
  };

  const fetchExpenseEntriesPage = async (requestId, page = 1, limit = EXPENSES_PAGE_SIZE) => {
    const response = await axios.get(`${apiurl}advance_payment.php`, {
      params: {
        fetch: "expenses_entries",
        request_id: requestId,
        page,
        limit,
      },
    });

    return {
      entries: Array.isArray(response.data?.data) ? response.data.data : [],
      pagination: response.data?.pagination || {
        page: 1,
        limit,
        total_items: 0,
        total_pages: 1,
      },
      summary: response.data?.summary || null,
    };
  };

  const fetchAllExpenseEntries = async (requestId) => {
    const firstPage = await fetchExpenseEntriesPage(requestId, 1, EXPENSES_PAGE_SIZE);
    let allEntries = [...firstPage.entries];
    const totalPages = Number(firstPage.pagination?.total_pages || 1);

    if (totalPages > 1) {
      for (let page = 2; page <= totalPages; page += 1) {
        const nextPage = await fetchExpenseEntriesPage(requestId, page, EXPENSES_PAGE_SIZE);
        allEntries = [...allEntries, ...nextPage.entries];
      }
    }

    return {
      entries: allEntries,
      pagination: firstPage.pagination,
      summary: firstPage.summary,
    };
  };

  const openExpensesModal = async (requestRow) => {
    setExpensesModal({
      open: true,
      request: requestRow,
      entries: [],
      errors: [],
      page: 1,
      loading: true,
    });

    try {
      const result = await fetchAllExpenseEntries(requestRow.id);
      const entries = result.entries.length > 0
        ? result.entries.map((entry) => ({
            ...createExpenseEntry(entry),
            amountSpent: entry.amount_spent ? formatAmountInput(entry.amount_spent) : "",
          }))
        : [createExpenseEntry({ purpose: requestRow.purposes?.[0] || "" })];

      setExpensesModal({
        open: true,
        request: requestRow,
        entries,
        errors: entries.map(() => ({})),
        page: 1,
        loading: false,
      });
    } catch (error) {
      setExpensesModal(initialExpensesModal);
      await Swal.fire("Error", "Unable to load expenses rows.", "error");
    }
  };

  const closeExpensesModal = () => {
    setExpensesModal(initialExpensesModal);
  };

  const loadExpensesViewPage = async (requestRow, page = 1) => {
    setExpensesViewModal((prev) => ({
      ...prev,
      open: true,
      request: requestRow,
      loading: true,
    }));

    try {
      const result = await fetchExpenseEntriesPage(requestRow.id, page, EXPENSES_PAGE_SIZE);
      setExpensesViewModal({
        open: true,
        request: requestRow,
        entries: result.entries,
        page: Number(result.pagination?.page || page),
        totalEntries: Number(result.pagination?.total_items || 0),
        totalPages: Number(result.pagination?.total_pages || 1),
        loading: false,
      });
    } catch (error) {
      setExpensesViewModal(initialExpensesViewModal);
      await Swal.fire("Error", "Unable to load expenses details.", "error");
    }
  };

  const openExpensesViewModal = async (requestRow) => {
    await loadExpensesViewPage(requestRow, 1);
  };

  const closeExpensesViewModal = () => {
    setExpensesViewModal(initialExpensesViewModal);
  };

  const openExpensesUploadModal = (requestRow) => {
    setExpensesUploadModal({
      open: true,
      request: requestRow,
      file: null,
      fileName: "",
      errors: [],
      generalError: "",
    });
  };

  const closeExpensesUploadModal = () => {
    setExpensesUploadModal(initialExpensesUploadModal);
  };

  const openDocumentsModal = (requestRow, canUpload) => {
    setDocumentsModal({
      open: true,
      request: requestRow,
      documents: Array.isArray(requestRow.expense_documents) ? requestRow.expense_documents : [],
      canUpload,
      uploading: false,
      error: "",
    });
  };

  const closeDocumentsModal = () => {
    setDocumentsModal(initialDocumentsModal);
  };

  const handleDocumentsUpload = async (files) => {
    if (!documentsModal.request || !files || files.length === 0) return;

    setDocumentsModal((prev) => ({ ...prev, uploading: true, error: "" }));

    const formData = new FormData();
    formData.append("action", "upload_expense_document");
    formData.append("request_id", String(documentsModal.request.id));
    Array.from(files).forEach((file) => formData.append("documents[]", file));

    try {
      const response = await axios.post(`${apiurl}advance_payment.php`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (!response.data?.status) {
        setDocumentsModal((prev) => ({
          ...prev,
          uploading: false,
          error: response.data?.message || "Failed to upload documents.",
        }));
        return;
      }

      const updatedDocuments = Array.isArray(response.data.documents) ? response.data.documents : [];
      const requestId = documentsModal.request.id;
      setDocumentsModal((prev) => ({
        ...prev,
        uploading: false,
        documents: updatedDocuments,
        error: Array.isArray(response.data.errors) && response.data.errors.length > 0 ? response.data.errors.join(" ") : "",
      }));

      setRequests((prev) =>
        prev.map((row) => (row.id === requestId ? { ...row, expense_documents: updatedDocuments } : row))
      );
    } catch (error) {
      setDocumentsModal((prev) => ({
        ...prev,
        uploading: false,
        error: "Failed to upload documents. Please try again.",
      }));
    }
  };

  const openAccountsModal = (requestRow) => {
    const draft = accountsDraftByRequest[requestRow.id] || { utr_no: "", remark: "" };
    setAccountsModal({
      open: true,
      request: requestRow,
      utrNo: draft.utr_no || requestRow.utr_no || "",
      remark: draft.remark || requestRow.accounts_remark || "",
      markPaid: false,
    });
  };

  const closeAccountsModal = () => {
    setAccountsModal(initialAccountsModal);
  };

  const openRecoveryModal = (requestRow) => {
    setRecoveryModal({
      open: true,
      request: requestRow,
      form: {
        estimatedRecoveryAmount: "",
        estimatedRecoveryDate: "",
        actualRecoveryAmount: "",
        actualRecoveryDate: "",
        nonRecoveryAmount: "",
        nonRecoveryReason: "",
        recoveryRemark: "",
      },
      errors: {},
    });
  };

  const closeRecoveryModal = () => {
    setRecoveryModal(initialRecoveryModal);
  };

  const openRecoveryViewModal = (requestRow) => {
    setRecoveryViewModal({
      open: true,
      request: requestRow,
    });
  };

  const closeRecoveryViewModal = () => {
    setRecoveryViewModal(initialRecoveryViewModal);
  };

  const handleDecisionRemarkChange = (event) => {
    const { value } = event.target;
    setDecisionModal((prev) => ({
      ...prev,
      remark: value,
      error: prev.error ? "" : prev.error,
    }));
  };

  const handleApprovalAction = async () => {
    const requestRow = decisionModal.request;
    const action = decisionModal.action;
    if (!requestRow) return;

    const isReject = action === "reject";
    const remark = decisionModal.remark.trim();

    if (isReject && !remark) {
      setDecisionModal((prev) => ({
        ...prev,
        error: "Rejection remark is required.",
      }));
      return;
    }

    try {
      setRequestBusy(requestRow.id, action);
      const formData = new FormData();
      formData.append("action", action);
      formData.append("request_id", String(requestRow.id));
      formData.append("remark", remark);

      const response = await axios.post(`${apiurl}advance_payment.php`, formData);
      if (response.data?.status === true) {
        closeDecisionModal();
        await fetchData();
        triggerRefresh();
        await Swal.fire("Success", response.data.message || "Updated successfully.", "success");
      } else {
        await Swal.fire("Error", response.data?.message || "Server Error", "error");
      }
    } catch (error) {
      await Swal.fire("Error", "Server Error", "error");
    } finally {
      setRequestBusy(requestRow.id, "");
    }
  };

  const handleSpocFieldChange = (event) => {
    const { name, value } = event.target;
    let nextValue = value;
    if (name === "amountTransferred") {
      nextValue = formatAmountInput(value);
    } else if (name === "spocMobile") {
      nextValue = sanitizeMobileInput(value);
    } else if (name === "spocEmail") {
      nextValue = value.trimStart();
    }
    setSpocModal((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        [name]: nextValue,
      },
      errors: prev.errors[name]
        ? {
            ...prev.errors,
            [name]: "",
          }
        : prev.errors,
    }));
  };

  const handleSpocSubmit = async (event) => {
    event.preventDefault();
    if (!spocModal.request) return;

    const validationErrors = validateSpocForm(spocModal.form);
    if (Object.keys(validationErrors).length > 0) {
      setSpocModal((prev) => ({
        ...prev,
        errors: validationErrors,
      }));
      return;
    }

    try {
      setRequestBusy(spocModal.request.id, "update_spoc");
      const formData = new FormData();
      formData.append("action", "update_spoc");
      formData.append("request_id", String(spocModal.request.id));
      formData.append("spoc_name", spocModal.form.spocName.trim());
      formData.append("spoc_mobile", spocModal.form.spocMobile.trim());
      formData.append("spoc_email", spocModal.form.spocEmail.trim());
      formData.append("amount_transferred", parseAmountInput(spocModal.form.amountTransferred));
      formData.append("transferred_date", spocModal.form.transferredDate);
      formData.append("transfer_cause", spocModal.form.transferCause.trim());

      const response = await axios.post(`${apiurl}advance_payment.php`, formData);
      if (response.data?.status === true) {
        closeSpocModal();
        await fetchData();
        triggerRefresh();
        await Swal.fire("Success", response.data.message || "SPOC details updated.", "success");
      } else {
        await Swal.fire("Error", response.data?.message || "Server Error", "error");
      }
    } catch (error) {
      await Swal.fire("Error", "Server Error", "error");
    } finally {
      setRequestBusy(spocModal.request.id, "");
    }
  };

  const handleExpenseSpentOnChange = (index, value) => {
    setExpensesModal((prev) => {
      const nextEntries = [...prev.entries];
      nextEntries[index] = {
        ...nextEntries[index],
        spentOn: value,
        candidateName: value === "self" ? (sessionUser?.name || "") : "",
        mobileNumber: value === "self" ? (sessionUser?.mobile || "") : "",
      };

      const nextErrors = [...prev.errors];
      nextErrors[index] = {
        ...(nextErrors[index] || {}),
        candidateName: "",
        mobileNumber: "",
      };

      return {
        ...prev,
        entries: nextEntries,
        errors: nextErrors,
      };
    });
  };

  const handleExpenseEntryChange = (index, key, value) => {
    setExpensesModal((prev) => {
      const nextEntries = [...prev.entries];
      let nextValue = value;

      if (key === "amountSpent") {
        nextValue = formatAmountInput(value);
      } else if (key === "mobileNumber") {
        nextValue = sanitizeMobileInput(value);
      } else if (key === "aadhaarNumber") {
        nextValue = sanitizeAadhaarInput(value);
      }

      nextEntries[index] = {
        ...nextEntries[index],
        [key]: nextValue,
      };

      const nextErrors = [...prev.errors];
      nextErrors[index] = {
        ...(nextErrors[index] || {}),
        [key]: "",
      };

      return {
        ...prev,
        entries: nextEntries,
        errors: nextErrors,
      };
    });
  };

  const addExpenseEntry = () => {
    setExpensesModal((prev) => {
      const nextEntries = [...prev.entries, createExpenseEntry()];
      return {
        ...prev,
        entries: nextEntries,
        errors: [...prev.errors, {}],
        page: Math.max(1, Math.ceil(nextEntries.length / EXPENSES_PAGE_SIZE)),
      };
    });
  };

  const removeExpenseEntry = (index) => {
    setExpensesModal((prev) => {
      if (prev.entries.length === 1) return prev;

      const nextEntries = prev.entries.filter((_, entryIndex) => entryIndex !== index);
      const nextErrors = prev.errors.filter((_, entryIndex) => entryIndex !== index);
      const nextPageCount = Math.max(1, Math.ceil(nextEntries.length / EXPENSES_PAGE_SIZE));

      return {
        ...prev,
        entries: nextEntries,
        errors: nextErrors,
        page: Math.min(prev.page, nextPageCount),
      };
    });
  };

  const updateExpensesModalPage = (nextPage) => {
    setExpensesModal((prev) => {
      const totalPages = Math.max(1, Math.ceil(prev.entries.length / EXPENSES_PAGE_SIZE));
      return {
        ...prev,
        page: Math.min(Math.max(nextPage, 1), totalPages),
      };
    });
  };

  const updateExpensesViewModalPage = (nextPage) => {
    const requestRow = expensesViewModal.request;
    const totalPages = Math.max(1, Number(expensesViewModal.totalPages || 1));
    const safePage = Math.min(Math.max(nextPage, 1), totalPages);
    if (!requestRow || safePage === expensesViewModal.page) return;
    loadExpensesViewPage(requestRow, safePage);
  };

  const renderExpensesPagination = (currentPage, totalItems, onPageChange, pageSize = EXPENSES_PAGE_SIZE) => {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    if (totalPages <= 1) {
      return null;
    }

    const pageNumbers = Array.from({ length: totalPages }, (_, index) => index + 1);

    return (
      <div className="advance-payment-pagination">
        <div className="advance-payment-pagination-actions">
          <button
            type="button"
            className="advance-payment-secondary-btn"
            onClick={() => onPageChange(1)}
            disabled={currentPage <= 1}
          >
            First
          </button>
          <button
            type="button"
            className="advance-payment-secondary-btn"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1}
          >
            Prev
          </button>
          {pageNumbers.map((pageNumber) => (
            <button
              key={`expense-page-${pageNumber}`}
              type="button"
              className={`advance-payment-page-btn ${pageNumber === currentPage ? "active" : ""}`}
              onClick={() => onPageChange(pageNumber)}
            >
              {pageNumber}
            </button>
          ))}
          <button
            type="button"
            className="advance-payment-secondary-btn"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
          >
            Next
          </button>
          <button
            type="button"
            className="advance-payment-secondary-btn"
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage >= totalPages}
          >
            Last
          </button>
        </div>
        <div className="advance-payment-pagination-meta">
          Page {currentPage} of {totalPages}
        </div>
      </div>
    );
  };

  const changeMainSectionPage = (sectionKey, nextPage) => {
    setSectionPages((prev) => {
      const totalPages = Math.max(1, Number(mainPagination.totalPages || 1));
      return {
        ...prev,
        [sectionKey]: Math.min(Math.max(nextPage, 1), totalPages),
      };
    });
  };

  const renderMainTablePagination = (sectionKey) => renderExpensesPagination(
    Number(mainPagination.page || 1),
    Number(mainPagination.totalItems || 0),
    (nextPage) => changeMainSectionPage(sectionKey, nextPage),
    Number(mainPagination.limit || MAIN_TABLE_PAGE_SIZE)
  );

  const handleExpensesSubmit = async (event) => {
    event.preventDefault();
    if (!expensesModal.request) return;

    const validationErrors = validateExpenseEntries(expensesModal.entries);
    if (validationErrors.some((rowErrors) => Object.keys(rowErrors).length > 0)) {
      setExpensesModal((prev) => ({
        ...prev,
        errors: validationErrors,
      }));
      return;
    }

    const payloadEntries = expensesModal.entries.map((entry) => ({
      entry_id: entry.entryId,
      spent_on: entry.spentOn,
      purpose: entry.purpose.trim(),
      expense_date: entry.expenseDate,
      candidate_name: entry.candidateName.trim(),
      mobile_number: entry.mobileNumber.trim(),
      aadhaar_number: entry.aadhaarNumber.trim(),
      amount_spent: parseAmountInput(entry.amountSpent),
      reason: entry.reason.trim(),
    }));

    try {
      setRequestBusy(expensesModal.request.id, "update_expenses");
      const formData = new FormData();
      formData.append("action", "update_expenses");
      formData.append("request_id", String(expensesModal.request.id));
      formData.append("expenses_entries", JSON.stringify(payloadEntries));

      const response = await axios.post(`${apiurl}advance_payment.php`, formData);
      if (response.data?.status === true) {
        closeExpensesModal();
        await fetchData();
        triggerRefresh();
        await Swal.fire("Success", response.data.message || "Expenses updated.", "success");
      } else {
        await Swal.fire("Error", response.data?.message || "Server Error", "error");
      }
    } catch (error) {
      await Swal.fire("Error", "Server Error", "error");
    } finally {
      setRequestBusy(expensesModal.request.id, "");
    }
  };

  const handleExpensesUploadFileChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;
    setExpensesUploadModal((prev) => ({
      ...prev,
      file: selectedFile,
      fileName: selectedFile?.name || "",
      errors: [],
      generalError: "",
    }));
  };

  const handleDownloadExpenseDemoCsv = () => {
    const csvRows = [
      ["spent_on", "purpose", "expense_date", "candidate_name", "mobile_number", "aadhaar_number", "amount_spent", "reason"],
      ["candidate", "Travel", "2026-05-04", "Rahul Sharma", "9876543210", "123456789012", "2500.00", "Candidate visit"],
      ["self", "Local Travel", "2026-05-05", "", "", "123456789012", "500.00", "Own cab fare"],
    ];

    const csvContent = csvRows
      .map((row) => row.map((value) => `"${String(value).replace(/"/g, "\"\"")}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = "advance-expenses-demo.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  };

  const handleExpensesBulkUpload = async (event) => {
    event.preventDefault();
    if (!expensesUploadModal.request) return;

    if (!expensesUploadModal.file) {
      setExpensesUploadModal((prev) => ({
        ...prev,
        generalError: "Please choose a CSV file to upload.",
        errors: [],
      }));
      return;
    }

    try {
      setRequestBusy(expensesUploadModal.request.id, "bulk_upload_expenses");
      const formData = new FormData();
      formData.append("action", "bulk_upload_expenses");
      formData.append("request_id", String(expensesUploadModal.request.id));
      formData.append("expenses_csv", expensesUploadModal.file);

      const response = await axios.post(`${apiurl}advance_payment.php`, formData);
      if (response.data?.status === true) {
        closeExpensesUploadModal();
        await fetchData();
        triggerRefresh();
        await Swal.fire("Success", response.data.message || "Expenses uploaded successfully.", "success");
      } else {
        setExpensesUploadModal((prev) => ({
          ...prev,
          errors: Array.isArray(response.data?.errors) ? response.data.errors : [],
          generalError: response.data?.message || "Bulk upload failed.",
        }));
      }
    } catch (error) {
      setExpensesUploadModal((prev) => ({
        ...prev,
        errors: [],
        generalError: "Server Error",
      }));
    } finally {
      setRequestBusy(expensesUploadModal.request.id, "");
    }
  };

  const handleAccountsModalSubmit = async (event) => {
    event.preventDefault();
    if (!accountsModal.request) return;

    const requestRow = accountsModal.request;
    const markPaid = accountsModal.markPaid;
    const busyKey = markPaid ? "mark_paid" : "save_utr";

    const reuseInfo = requestRow.reuse_info || {};
    const isZeroFreshAsk = reuseInfo.is_reuse && Number(reuseInfo.effective_ask_amount || 0) <= 0;
    if (!accountsModal.utrNo.trim() && !isZeroFreshAsk) {
      await Swal.fire("Warning", "UTR number is required.", "warning");
      return;
    }

    try {
      setRequestBusy(requestRow.id, busyKey);

      const saveFormData = new FormData();
      saveFormData.append("action", "save_utr");
      saveFormData.append("request_id", String(requestRow.id));
      saveFormData.append("utr_no", accountsModal.utrNo.trim());
      saveFormData.append("remark", accountsModal.remark.trim());

      const saveResponse = await axios.post(`${apiurl}advance_payment.php`, saveFormData);
      if (saveResponse.data?.status !== true) {
        await Swal.fire("Error", saveResponse.data?.message || "Server Error", "error");
        return;
      }

      if (markPaid) {
        const confirm = await Swal.fire({
          title: "Mark as paid?",
          text: "This will complete the request and move it into tracking.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Mark Paid",
          confirmButtonColor: "#0f766e",
        });

        if (!confirm.isConfirmed) {
          await fetchData();
          triggerRefresh();
          closeAccountsModal();
          await Swal.fire("Success", saveResponse.data.message || "UTR saved.", "success");
          return;
        }

        const paidFormData = new FormData();
        paidFormData.append("action", "mark_paid");
        paidFormData.append("request_id", String(requestRow.id));
        paidFormData.append("remark", accountsModal.remark.trim());

        const paidResponse = await axios.post(`${apiurl}advance_payment.php`, paidFormData);
        if (paidResponse.data?.status !== true) {
          await Swal.fire("Error", paidResponse.data?.message || "Server Error", "error");
          return;
        }

        closeAccountsModal();
        await fetchData();
        triggerRefresh();
        await Swal.fire("Success", paidResponse.data.message || "Payment marked as paid.", "success");
        return;
      }

      closeAccountsModal();
      await fetchData();
      triggerRefresh();
      await Swal.fire("Success", saveResponse.data.message || "UTR saved.", "success");
    } catch (error) {
      await Swal.fire("Error", "Server Error", "error");
    } finally {
      setRequestBusy(requestRow.id, "");
    }
  };

  const handleRecoveryFieldChange = (event) => {
    const { name, value } = event.target;
    const amountFields = ["estimatedRecoveryAmount", "actualRecoveryAmount", "nonRecoveryAmount"];
    const nextValue = amountFields.includes(name) ? formatAmountInput(value) : value;

    setRecoveryModal((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        [name]: nextValue,
      },
      errors: {
        ...prev.errors,
        [name]: "",
        form: "",
      },
    }));
  };

  const handleRecoverySubmit = async (event) => {
    event.preventDefault();
    if (!recoveryModal.request) return;

    const validationErrors = validateRecoveryForm(
      recoveryModal.form,
      recoveryModal.request.recovery_summary?.remaining_recovery_capacity || 0
    );
    if (Object.keys(validationErrors).length > 0) {
      setRecoveryModal((prev) => ({
        ...prev,
        errors: validationErrors,
      }));
      return;
    }

    try {
      setRequestBusy(recoveryModal.request.id, "update_recovery");
      const formData = new FormData();
      formData.append("action", "update_recovery");
      formData.append("request_id", String(recoveryModal.request.id));
      formData.append("estimated_recovery_amount", parseAmountInput(recoveryModal.form.estimatedRecoveryAmount));
      formData.append("estimated_recovery_date", recoveryModal.form.estimatedRecoveryDate);
      formData.append("actual_recovery_amount", parseAmountInput(recoveryModal.form.actualRecoveryAmount));
      formData.append("actual_recovery_date", recoveryModal.form.actualRecoveryDate);
      formData.append("non_recovery_amount", parseAmountInput(recoveryModal.form.nonRecoveryAmount));
      formData.append("non_recovery_reason", recoveryModal.form.nonRecoveryReason.trim());
      formData.append("recovery_remark", recoveryModal.form.recoveryRemark.trim());

      const response = await axios.post(`${apiurl}advance_payment.php`, formData);
      if (response.data?.status === true) {
        closeRecoveryModal();
        await fetchData();
        triggerRefresh();
        await Swal.fire("Success", response.data.message || "Recovery entry added.", "success");
      } else {
        await Swal.fire("Error", response.data?.message || "Server Error", "error");
      }
    } catch (error) {
      await Swal.fire("Error", "Server Error", "error");
    } finally {
      setRequestBusy(recoveryModal.request.id, "");
    }
  };

  const loadMarkRecoveredPage = async (requestRow, page = 1) => {
    setMarkRecoveredModal((prev) => ({
      ...prev,
      open: true,
      request: requestRow,
      loading: true,
      error: "",
    }));

    try {
      const result = await fetchExpenseEntriesPage(requestRow.id, page, EXPENSES_PAGE_SIZE);
      setMarkRecoveredModal((prev) => {
        const nextEntriesById = { ...prev.entriesById };

        result.entries.forEach((entry) => {
          const entryId = entry.entry_id;
          if (!entryId) return;
          nextEntriesById[entryId] = entry;
        });

        return {
          ...prev,
          open: true,
          request: requestRow,
          entries: result.entries,
          entriesById: nextEntriesById,
          page: Number(result.pagination?.page || page),
          totalEntries: Number(result.pagination?.total_items || 0),
          totalPages: Number(result.pagination?.total_pages || 1),
          loading: false,
        };
      });
    } catch (error) {
      setMarkRecoveredModal(initialMarkRecoveredModal);
      await Swal.fire("Error", "Unable to load expenses for this request.", "error");
    }
  };

  const openMarkRecoveredModal = async (requestRow) => {
    setMarkRecoveredModal(initialMarkRecoveredModal);
    await loadMarkRecoveredPage(requestRow, 1);
  };

  const closeMarkRecoveredModal = () => {
    setMarkRecoveredModal(initialMarkRecoveredModal);
  };

  const updateMarkRecoveredPage = (nextPage) => {
    if (!markRecoveredModal.request) return;
    loadMarkRecoveredPage(markRecoveredModal.request, nextPage);
  };

  const handleMarkRecoveredValueChange = (entryId, value, remaining) => {
    setMarkRecoveredModal((prev) => {
      const numericValue = Number(value);
      const rowError = value !== "" && Number.isFinite(numericValue) && numericValue > remaining
        ? `Cannot exceed remaining recoverable amount (${formatAmount(remaining)}).`
        : "";
      return {
        ...prev,
        values: { ...prev.values, [entryId]: value },
        touched: { ...prev.touched, [entryId]: true },
        rowErrors: { ...prev.rowErrors, [entryId]: rowError },
      };
    });
  };

  const handleMarkRecoveredSubmit = async () => {
    if (!markRecoveredModal.request) return;

    const hasRowErrors = Object.values(markRecoveredModal.rowErrors).some((message) => message);
    if (hasRowErrors) {
      setMarkRecoveredModal((prev) => ({ ...prev, error: "Please fix the amounts marked in red before submitting." }));
      return;
    }

    const items = [];
    Object.keys(markRecoveredModal.touched).forEach((entryId) => {
      if (!markRecoveredModal.touched[entryId]) return;
      const rawValue = markRecoveredModal.values[entryId];
      const entry = markRecoveredModal.entriesById[entryId];
      if (!entry || rawValue === "" || rawValue === undefined) return;

      const numericValue = Number(rawValue);
      if (!Number.isFinite(numericValue) || numericValue <= 0) return;

      items.push({ entry_id: entryId, recovered_amount: numericValue });
    });

    if (items.length === 0) {
      setMarkRecoveredModal((prev) => ({ ...prev, error: "Please enter at least one recovered amount before submitting." }));
      return;
    }

    try {
      setMarkRecoveredModal((prev) => ({ ...prev, submitting: true, error: "" }));
      const formData = new FormData();
      formData.append("action", "mark_expense_recovered");
      formData.append("request_id", String(markRecoveredModal.request.id));
      formData.append("items", JSON.stringify(items));
      formData.append("remark", markRecoveredModal.remark.trim());

      const response = await axios.post(`${apiurl}advance_payment.php`, formData);
      if (response.data?.status === true) {
        closeMarkRecoveredModal();
        await fetchData();
        triggerRefresh();
        await Swal.fire("Success", response.data.message || "Recovery marked.", "success");
      } else {
        setMarkRecoveredModal((prev) => ({ ...prev, submitting: false, error: response.data?.message || "Server Error" }));
      }
    } catch (error) {
      setMarkRecoveredModal((prev) => ({ ...prev, submitting: false, error: "Server Error" }));
    }
  };

  const renderStatusBadge = (label, type) => (
    <span className={`advance-payment-status-badge ${type || "secondary"}`}>{label}</span>
  );

  const renderApprovalRowActions = (row) => {
    const busyState = busyByRequest[row.id] || "";
    const isAccountsStage = row.current_stage === "accounts";
    const hasTransferredAmount = Number(row.spoc_amount_transferred || 0) > 0;
    const hasSpocDetails = Boolean(
      row.spoc_name || row.spoc_mobile || row.spoc_email || hasTransferredAmount || row.spoc_transferred_date || row.spoc_transfer_cause
    );
    const canUpdateSpoc = row.is_mine && row.overall_status === "paid" && !hasSpocDetails;
    const canViewSpoc = row.overall_status === "paid" && hasSpocDetails && (row.is_mine || ["accounts", "accounts_head"].includes(sessionUser?.post || ""));

    if (canViewSpoc) {
      return (
        <div className="advance-payment-inline-actions">
          <button
            type="button"
            className="advance-payment-primary-btn"
            onClick={() => openSpocViewModal(row)}
          >
            View SPOC Details
          </button>
        </div>
      );
    }

    if (canUpdateSpoc) {
      return (
        <div className="advance-payment-inline-actions">
          <button
            type="button"
            className="advance-payment-primary-btn"
            onClick={() => openSpocModal(row)}
            disabled={busyState === "update_spoc"}
          >
            {busyState === "update_spoc" ? "Updating..." : "Update SPOC"}
          </button>
        </div>
      );
    }

    if (isAccountsStage && row.can_accounts_update) {
      const isPaid = row.overall_status === "paid";

      return (
        <div className="advance-payment-inline-actions">
          <button
            type="button"
            className="advance-payment-primary-btn"
            onClick={() => openAccountsModal(row)}
            disabled={busyState === "save_utr" || busyState === "mark_paid" || isPaid}
          >
            {busyState === "save_utr" || busyState === "mark_paid" ? "Updating..." : "Update Details"}
          </button>
        </div>
      );
    }

    if (row.overall_status === "paid" || row.overall_status === "rejected" || row.current_stage === "completed") {
      return <span className="advance-payment-muted-text">-</span>;
    }

    return (
      <div className="advance-payment-inline-actions">
            <button
              type="button"
              className="advance-payment-secondary-btn danger"
              onClick={() => openDecisionModal(row, "reject")}
              disabled={busyState === "reject" || busyState === "approve"}
            >
              {busyState === "reject" ? "Rejecting..." : "Reject"}
        </button>
            <button
              type="button"
              className="advance-payment-primary-btn"
              onClick={() => openDecisionModal(row, "approve")}
              disabled={busyState === "approve" || busyState === "reject"}
            >
              {busyState === "approve" ? "Approving..." : "Approve"}
        </button>
      </div>
    );
  };

  const renderPlaceholderContent = () => (
    <div className="advance-payment-empty">
      <i className={activeMeta.emptyIcon} />
      <strong>{activeMeta.label}</strong>
      <span>Provision is ready for the next phase.</span>
    </div>
  );

  const renderExpensesSection = () => (
    <>
      <div className="advance-payment-action-card">
        <div className="advance-payment-action-main">
          <div className="advance-payment-action-icon">
            <i className="ri-line-chart-line" />
          </div>
          <div>
            <h3>Expenses Tracking</h3>
            <p>
              Paid advance requests appear here so the requester can update how the amount was spent.
            </p>
          </div>
        </div>
        {sessionUser?.post && ["cxo", "accounts", "accounts_head", "admin"].includes(sessionUser.post) ? (
          <button
            type="button"
            className="advance-payment-primary-btn"
            onClick={() => setDownloadModal({ open: true, section: "expenses", requesterMode: "all", requesterIds: [], fromDate: "", toDate: "", submitting: false })}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <i className="ri-download-2-line" />
            Download Summary
          </button>
        ) : null}
      </div>

      <div className="d-flex flex-wrap gap-2 align-items-center mt-3 mb-3" style={{ rowGap: 8 }}>
        <form
          onSubmit={(e) => { e.preventDefault(); setAppliedExpensesSearch(expensesSearchInput.trim()); setSectionPages((prev) => ({ ...prev, expenses: 1 })); }}
          className="d-flex"
          style={{ flex: "1 1 320px", maxWidth: 480 }}
        >
          <input
            type="text"
            className="form-control"
            placeholder="Search request, requester, client, location..."
            value={expensesSearchInput}
            onChange={(e) => setExpensesSearchInput(e.target.value)}
            style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
          />
          {appliedExpensesSearch ? (
            <button
              type="button"
              className="btn btn-light"
              style={{ borderRadius: 0, borderLeft: 0, borderRight: 0 }}
              onClick={() => { setExpensesSearchInput(""); setAppliedExpensesSearch(""); setSectionPages((prev) => ({ ...prev, expenses: 1 })); }}
              title="Clear search"
            >
              <i className="ri-close-line" />
            </button>
          ) : null}
          <button
            type="submit"
            className="advance-payment-primary-btn"
            style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
          >
            <i className="ri-search-line" /> Search
          </button>
        </form>

        {sessionUser?.post && ["cxo", "accounts", "accounts_head", "admin", "founder"].includes(sessionUser.post) ? (
          <div style={{ flex: "1 1 280px", maxWidth: 420 }}>
            <Select
              isMulti
              isClearable
              isSearchable
              placeholder="Filter by requester..."
              classNamePrefix="react-select"
              options={requesterOptions.map((r) => ({ value: r.id, label: r.name + (r.post ? ` (${r.post.replace(/_/g, " ")})` : "") }))}
              value={expensesRequesterFilter
                .map((id) => {
                  const found = requesterOptions.find((r) => r.id === id);
                  return found ? { value: found.id, label: found.name + (found.post ? ` (${found.post.replace(/_/g, " ")})` : "") } : null;
                })
                .filter(Boolean)}
              onChange={(opts) => {
                setExpensesRequesterFilter((opts || []).map((o) => o.value));
                setSectionPages((prev) => ({ ...prev, expenses: 1 }));
              }}
              menuPortalTarget={selectMenuPortalTarget}
              styles={selectMenuPortalStyles}
            />
          </div>
        ) : null}
      </div>

      <div className="advance-payment-table-shell">
        <div className="advance-payment-table advance-payment-table--expenses table-responsive" style={{ overflowX: "auto" }}>
          <table className="table table-bordered align-middle mb-0" style={{ minWidth: 1150 }}>
            <thead>
              <tr>
                <th>S.No</th>
                <th>Request</th>
                <th>Requester</th>
                <th>Client</th>
                <th>Amount Received</th>
                <th>Expenses Updated</th>
                <th>Action</th>
                <th>Documents</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="text-center py-4 text-muted">Loading...</td>
                </tr>
              ) : requests.length > 0 ? (
                requests.map((row, index) => {
                  const serialNumber = ((Number(mainPagination.page || 1) - 1) * MAIN_TABLE_PAGE_SIZE) + index + 1;
                  const entriesCount = Number(row.expenses_summary?.entries_count || 0);
                  const totalSpent = row.expenses_summary?.total_spent || "0.00";
                  const busyState = busyByRequest[row.id] || "";
                  const overdueStyle = row.expense_overdue_level === "critical"
                    ? { background: "#fecaca" }
                    : row.expense_overdue_level === "warning"
                      ? { background: "#fee2e2" }
                      : null;

                  return (
                    <tr key={`expenses-row-${row.id}`} style={overdueStyle || undefined} title={overdueStyle ? `Expense pending for ${row.expense_overdue_days} days since payment` : undefined}>
                      <td>{serialNumber}</td>
                      <td>
                        <div className="advance-payment-code-cell">
                          <strong>{row.request_code || "-"}</strong>
                          <small>{formatDateTime(row.payment_marked_at || row.updated_at)}</small>
                        </div>
                      </td>
                      <td>
                        <div className="advance-payment-cell-stack">
                          <strong>{row.created_by_name || "-"}</strong>
                          <span>{(row.created_by_post || "").replace(/_/g, " ")}</span>
                        </div>
                      </td>
                      <td>
                        <div className="advance-payment-cell-stack">
                          <strong>{row.client_name || "-"}</strong>
                          <span>{row.location_name || "-"}</span>
                        </div>
                      </td>
                      <td>{formatAmount(row.amount)}</td>
                      <td>
                        <div className="advance-payment-cell-stack">
                          <strong>
                            {entriesCount > 0 ? `${entriesCount} entr${entriesCount === 1 ? "y" : "ies"}` : "Not Updated"}
                            {row.expense_overdue_level === "critical" ? (
                              <span className="badge bg-danger ms-1" style={{ fontSize: 10 }}>
                                <i className="ri-alarm-warning-fill" /> {row.expense_overdue_days}d overdue
                              </span>
                            ) : row.expense_overdue_level === "warning" ? (
                              <span className="badge bg-warning text-dark ms-1" style={{ fontSize: 10 }}>
                                <i className="ri-time-fill" /> {row.expense_overdue_days}d overdue
                              </span>
                            ) : null}
                          </strong>
                          <span>{entriesCount > 0 ? `Spent ${formatAmount(totalSpent)}` : "No expense rows added yet"}</span>
                        </div>
                      </td>
                      <td>
                        {row.is_mine ? (
                          <div className="advance-payment-table-actions">
                            <button
                              type="button"
                              className="advance-payment-primary-btn"
                              onClick={() => openExpensesModal(row)}
                              disabled={busyState === "update_expenses"}
                            >
                              {busyState === "update_expenses" ? "Updating..." : "Update Expenses"}
                            </button>
                            <button
                              type="button"
                              className="advance-payment-secondary-btn"
                              onClick={() => openExpensesUploadModal(row)}
                              disabled={busyState === "bulk_upload_expenses"}
                            >
                              {busyState === "bulk_upload_expenses" ? "Uploading..." : "Bulk Upload"}
                            </button>
                          </div>
                        ) : (
                          <span className="advance-payment-muted-text">-</span>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="advance-payment-link-btn"
                          onClick={() => openDocumentsModal(row, row.is_mine)}
                        >
                          {row.is_mine ? "Upload" : "View Documents"}
                          {Array.isArray(row.expense_documents) && row.expense_documents.length > 0
                            ? ` (${row.expense_documents.length})`
                            : ""}
                        </button>
                      </td>
                      <td className="advance-payment-details-toggle-cell">
                        {entriesCount > 0 ? (
                          <button
                            type="button"
                            className="advance-payment-link-btn"
                            onClick={() => openExpensesViewModal(row)}
                          >
                            View
                          </button>
                        ) : (
                          <span className="advance-payment-muted-text">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="9" className="text-center py-4 text-muted">No paid requests found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {!loading ? renderMainTablePagination("expenses") : null}
      </div>
    </>
  );

  const renderRecoverySection = () => (
    <>
      <div className="advance-payment-action-card">
        <div className="advance-payment-action-main">
          <div className="advance-payment-action-icon">
            <i className="ri-refund-2-line" />
          </div>
          <div className="advance-payment-copy-wide">
            <h3>Recovery</h3>
            <p>
              Track leftover, recoverable, and non-recoverable amounts with a proper recovery entry history.
            </p>
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap gap-2 align-items-end mt-3 mb-3" style={{ rowGap: 8 }}>
        <form
          onSubmit={(e) => { e.preventDefault(); setAppliedRecoverySearch(recoverySearchInput.trim()); setSectionPages((prev) => ({ ...prev, recovery: 1 })); }}
          className="d-flex"
          style={{ flex: "1 1 280px", maxWidth: 420 }}
        >
          <input
            type="text"
            className="form-control"
            placeholder="Search request, requester, client, location..."
            value={recoverySearchInput}
            onChange={(e) => setRecoverySearchInput(e.target.value)}
            style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
          />
          {appliedRecoverySearch ? (
            <button
              type="button"
              className="btn btn-light"
              style={{ borderRadius: 0, borderLeft: 0, borderRight: 0 }}
              onClick={() => { setRecoverySearchInput(""); setAppliedRecoverySearch(""); setSectionPages((prev) => ({ ...prev, recovery: 1 })); }}
              title="Clear search"
            >
              <i className="ri-close-line" />
            </button>
          ) : null}
          <button
            type="submit"
            className="advance-payment-primary-btn"
            style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
          >
            <i className="ri-search-line" /> Search
          </button>
        </form>

        <div className="advance-payment-filter-field" style={{ width: 200 }}>
          <label className="advance-payment-filter-label">Client</label>
          <Select
            classNamePrefix="react-select"
            isClearable
            placeholder="All Clients"
            options={recoveryClientSelectOptions}
            value={recoveryClientSelectOptions.find((option) => option.value === recoveryFilters.clientName) || null}
            onChange={(option) => handleRecoveryFilterChange("clientName", option)}
          />
        </div>

        <div className="advance-payment-filter-field" style={{ width: 200 }}>
          <label className="advance-payment-filter-label">Location</label>
          <Select
            classNamePrefix="react-select"
            isClearable
            placeholder="All Locations"
            options={recoveryLocationSelectOptions}
            value={recoveryLocationSelectOptions.find((option) => option.value === recoveryFilters.location) || null}
            onChange={(option) => handleRecoveryFilterChange("location", option)}
          />
        </div>

        <button
          type="button"
          className="advance-payment-secondary-btn"
          onClick={resetRecoveryFilters}
          disabled={!recoveryFilters.clientName && !recoveryFilters.location}
        >
          Reset Filters
        </button>
      </div>

      <div className="advance-payment-table-shell">
        <div className="advance-payment-table advance-payment-table--recovery table-responsive">
          <table className="table table-bordered align-middle mb-0">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Request</th>
              <th>Requester</th>
              <th className="advance-payment-recovery-client-col">Client</th>
              <th>Amount Received</th>
              <th>Total Spent</th>
              <th>Leftover Amount</th>
              <th>Recoverable Amount</th>
              <th>Non-Recoverable</th>
              <th>Recovered Amount</th>
              <th>Action</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="12" className="text-center py-4 text-muted">Loading...</td>
              </tr>
            ) : requests.length > 0 ? (
              requests.map((row, index) => {
                const serialNumber = ((Number(mainPagination.page || 1) - 1) * MAIN_TABLE_PAGE_SIZE) + index + 1;
                const busyState = busyByRequest[row.id] || "";

                return (
                  <tr key={`recovery-row-${row.id}`}>
                    <td>{serialNumber}</td>
                    <td>
                      <div className="advance-payment-code-cell">
                        <strong>{row.request_code || "-"}</strong>
                        <small>{formatDateTime(row.payment_marked_at || row.updated_at)}</small>
                      </div>
                    </td>
                    <td>
                      <div className="advance-payment-cell-stack">
                        <strong>{row.created_by_name || "-"}</strong>
                        <span>{(row.created_by_post || "").replace(/_/g, " ")}</span>
                      </div>
                    </td>
                    <td className="advance-payment-recovery-client-col">
                      <div className="advance-payment-cell-stack">
                        <strong>{row.client_name || "-"}</strong>
                        <span>{row.location_name || "-"}</span>
                      </div>
                    </td>
                    <td>{formatAmount(row.amount)}</td>
                    <td>{formatAmount(row.expenses_summary?.total_spent || 0)}</td>
                    <td>
                      {formatAmount(row.leftover_info?.available ?? row.recovery_summary?.leftover_amount ?? 0)}
                      {Number(row.leftover_info?.consumed || 0) > 0 ? (
                        <div
                          style={{ fontSize: 11, color: "#0e479c", marginTop: 2 }}
                          title={
                            Array.isArray(row.reuse_consumers) && row.reuse_consumers.length > 0
                              ? row.reuse_consumers.map((c) => `₹${Number(c.amount).toLocaleString("en-IN")} → ${c.request_code}`).join(", ")
                              : undefined
                          }
                        >
                          <i className="ri-recycle-line" /> ₹{Number(row.leftover_info.consumed).toLocaleString("en-IN")} reused
                          {Array.isArray(row.reuse_consumers) && row.reuse_consumers.length > 0
                            ? " into " + row.reuse_consumers.map((c) => c.request_code).join(", ")
                            : " elsewhere"}
                        </div>
                      ) : null}
                    </td>
                    <td>{formatAmount(row.recovery_summary?.recoverable_amount || 0)}</td>
                    <td>{formatAmount(row.recovery_summary?.non_recovery_amount || 0)}</td>
                    <td style={{ fontWeight: 600, color: "#0f766e" }}>{formatAmount(row.expense_recovered_total || 0)}</td>
                    <td>
                      {row.is_mine ? (
                        <button
                          type="button"
                          className="advance-payment-primary-btn"
                          onClick={() => openRecoveryModal(row)}
                          disabled={busyState === "update_recovery"}
                        >
                          {busyState === "update_recovery" ? "Saving..." : "Add Recovery"}
                        </button>
                      ) : row.can_mark_recovered ? (
                        <button
                          type="button"
                          className="advance-payment-primary-btn"
                          onClick={() => openMarkRecoveredModal(row)}
                        >
                          Mark Recovered
                        </button>
                      ) : (
                        <span className="advance-payment-muted-text">-</span>
                      )}
                    </td>
                    <td className="advance-payment-details-toggle-cell">
                      <button
                        type="button"
                        className="advance-payment-link-btn"
                        onClick={() => openRecoveryViewModal(row)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="12" className="text-center py-4 text-muted">No paid requests found.</td>
              </tr>
            )}
          </tbody>
          </table>
        </div>
        {!loading ? renderMainTablePagination("recovery") : null}
      </div>
    </>
  );

  return (
    <div className="main-content app-content">
      <style>{`
        .ap-swal-on-top.swal2-container,
        .ap-swal-on-top { z-index: 999999 !important; }
      `}</style>
      <div className="container-fluid mt-4 advance-payment-page">
        <div className="advance-payment-tabs" role="tablist" aria-label="Advance payment sections">
          {sections.map((section) => (
            <button
              key={section.key}
              type="button"
              className={`advance-payment-tab ${activeSection === section.key ? "active" : ""}`}
              onClick={() => setActiveSection(section.key)}
            >
              <i className={section.icon} />
              <span>{section.label}</span>
            </button>
          ))}
        </div>

        <div className="advance-payment-panel">
          {activeSection === "apply" ? (
            <>
              <div className="advance-payment-action-card">
                <div className="advance-payment-action-main">
                  <div className="advance-payment-action-icon">
                    <i className="ri-hand-coin-line" />
                  </div>
                  <div>
                    <h3>New advance request</h3>
                    <p>
                      Manage advance requests, approvals, and payment updates here.
                    </p>
                  </div>
                </div>

                <div className="d-flex gap-2 flex-wrap">
                  {sessionUser?.post && ["cxo", "accounts", "accounts_head", "admin"].includes(sessionUser.post) ? (
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={() => setDownloadModal({ open: true, section: "apply", requesterMode: "all", requesterIds: [], fromDate: "", toDate: "", submitting: false })}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                    >
                      <i className="ri-download-2-line" />
                      Download Summary
                    </button>
                  ) : null}
                  {canApply ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={openBankModal}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                      >
                        <i className={bankDetails?.verified_at ? "ri-bank-line" : "ri-error-warning-line"} />
                        {bankDetails?.verified_at ? "Update Bank Details" : "Add Bank Details"}
                        {bankDetails?.verified_at ? (
                          <span className="badge bg-success-transparent ms-1" style={{ fontSize: 10 }}>Verified</span>
                        ) : null}
                      </button>
                      <button
                        type="button"
                        className="advance-payment-primary-btn"
                        onClick={() => setShowApplyModal(true)}
                      >
                        <i className="ri-add-line" />
                        Apply for Advance
                      </button>
                    </>
                  ) : null}
                </div>
              </div>

              <div className="d-flex flex-wrap gap-2 align-items-center mt-3 mb-3" style={{ rowGap: 8 }}>
                <form
                  onSubmit={(e) => { e.preventDefault(); setAppliedApplySearch(applySearchInput.trim()); setSectionPages((prev) => ({ ...prev, apply: 1 })); }}
                  className="d-flex"
                  style={{ flex: "1 1 320px", maxWidth: 480 }}
                >
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search request, requester, client, location..."
                    value={applySearchInput}
                    onChange={(e) => setApplySearchInput(e.target.value)}
                    style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                  />
                  {appliedApplySearch ? (
                    <button
                      type="button"
                      className="btn btn-light"
                      style={{ borderRadius: 0, borderLeft: 0, borderRight: 0 }}
                      onClick={() => { setApplySearchInput(""); setAppliedApplySearch(""); setSectionPages((prev) => ({ ...prev, apply: 1 })); }}
                      title="Clear search"
                    >
                      <i className="ri-close-line" />
                    </button>
                  ) : null}
                  <button
                    type="submit"
                    className="advance-payment-primary-btn"
                    style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                  >
                    <i className="ri-search-line" /> Search
                  </button>
                </form>

                {sessionUser?.post && ["cxo", "accounts", "accounts_head", "admin", "founder"].includes(sessionUser.post) ? (
                  <div style={{ flex: "1 1 280px", maxWidth: 420 }}>
                    <Select
                      isMulti
                      isClearable
                      isSearchable
                      placeholder="Filter by requester..."
                      classNamePrefix="react-select"
                      options={requesterOptions.map((r) => ({ value: r.id, label: r.name + (r.post ? ` (${r.post.replace(/_/g, " ")})` : "") }))}
                      value={requesterFilter
                        .map((id) => {
                          const found = requesterOptions.find((r) => r.id === id);
                          return found ? { value: found.id, label: found.name + (found.post ? ` (${found.post.replace(/_/g, " ")})` : "") } : null;
                        })
                        .filter(Boolean)}
                      onChange={(opts) => {
                        setRequesterFilter((opts || []).map((o) => o.value));
                        setSectionPages((prev) => ({ ...prev, apply: 1 }));
                      }}
                      menuPortalTarget={selectMenuPortalTarget}
                      styles={selectMenuPortalStyles}
                    />
                  </div>
                ) : null}
              </div>

              <div className="advance-payment-table-shell">
                <div className="advance-payment-table table-responsive">
                  <table className="table table-bordered align-middle mb-0">
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Request</th>
                      <th>Requester</th>
                      <th>Client</th>
                      <th>Amount</th>
                      <th>Current Stage</th>
                      <th>Status</th>
                      <th>Action</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="9" className="text-center py-4 text-muted">Loading...</td>
                      </tr>
                    ) : requests.length > 0 ? (
                      requests.map((row, index) => {
                        const serialNumber = ((Number(mainPagination.page || 1) - 1) * MAIN_TABLE_PAGE_SIZE) + index + 1;

                        const overdueStyle = row.expense_overdue_level === "critical"
                          ? { background: "#fecaca" }
                          : row.expense_overdue_level === "warning"
                            ? { background: "#fee2e2" }
                            : null;
                        return (
                          <React.Fragment key={row.id}>
                            <tr style={overdueStyle || undefined} title={overdueStyle ? `Expense pending for ${row.expense_overdue_days} days since payment` : undefined}>
                              <td>{serialNumber}</td>
                              <td>
                                <div className="advance-payment-code-cell">
                                  <strong>{row.request_code || "-"}</strong>
                                  <small>{formatDateTime(row.created_at)}</small>
                                </div>
                              </td>
                              <td>
                                <div className="advance-payment-cell-stack">
                                  <strong>{row.created_by_name || "-"}</strong>
                                  <span>{(row.created_by_post || "").replace(/_/g, " ")}</span>
                                </div>
                              </td>
                              <td>
                                <div className="advance-payment-cell-stack">
                                  <strong>{row.client_name || "-"}</strong>
                                  <span>{row.location_name || "-"}</span>
                                </div>
                              </td>
                              <td>
                                {formatAmount(row.amount)}
                                {row.reuse_info?.is_reuse ? (
                                  <div style={{ fontSize: 11, color: "#0e479c", marginTop: 2 }}>
                                    <i className="ri-recycle-line" /> Reused ₹{Number(row.reuse_info.reused_amount).toLocaleString("en-IN")} from {row.reuse_info.reused_from_request_code}
                                  </div>
                                ) : null}
                              </td>
                              <td>{renderStatusBadge(stageLabels[row.current_stage] || "Completed", "primary")}</td>
                              <td>
                                <button
                                  type="button"
                                  className="advance-payment-link-btn"
                                  onClick={() => setStatusModal({ open: true, request: row })}
                                >
                                  View Status
                                </button>
                              </td>
                              <td>
                                {row.pending_for_me || (row.overall_status === "paid" && (row.is_mine || ["accounts", "accounts_head"].includes(sessionUser?.post || ""))) ? (
                                  renderApprovalRowActions(row)
                                ) : (
                                  <span className="advance-payment-muted-text">-</span>
                                )}
                              </td>
                              <td className="advance-payment-details-toggle-cell">
                                <button
                                  type="button"
                                  className="advance-payment-link-btn"
                                  onClick={() => setSelectedRequest(row)}
                                >
                                  View
                                </button>
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="9" className="text-center py-4 text-muted">No requests found.</td>
                      </tr>
                    )}
                  </tbody>
                  </table>
                </div>

                {!loading ? renderMainTablePagination("apply") : null}
              </div>
            </>
          ) : activeSection === "expenses" ? (
            renderExpensesSection()
          ) : activeSection === "recovery" ? (
            renderRecoverySection()
          ) : (
            renderPlaceholderContent()
          )}
        </div>
      </div>

      {showApplyModal && (
        <div className="advance-payment-modal-backdrop">
          <div className="advance-payment-modal advance-payment-apply-modal" role="dialog" aria-modal="true">
            <div className="advance-payment-modal-header">
              <h3>Apply for Advance</h3>
              <button type="button" onClick={resetAndCloseModal} aria-label="Close">
                <i className="ri-close-line" />
              </button>
            </div>

            <form onSubmit={handleSubmitRequest} noValidate>
              <div className="advance-payment-modal-body">
                <div className="mb-3">
                  <div className="advance-payment-form-label">Advance Purpose</div>
                  <div className="advance-payment-reason-grid">
                    {advanceReasons.map((reason) => (
                      <label className="advance-payment-check" key={reason}>
                        <input
                          type="checkbox"
                          checked={form.reasons.includes(reason)}
                          onChange={() => handleReasonToggle(reason)}
                        />
                        <span>{reason}</span>
                      </label>
                    ))}
                  </div>
                  {errors.reasons && <div className="advance-payment-error-text">{errors.reasons}</div>}
                </div>

                {isOtherSelected && (
                  <div className="mb-3">
                    <label className="advance-payment-form-label" htmlFor="advanceOtherCause">
                      Mention the cause
                    </label>
                    <input
                      className="form-control"
                      id="advanceOtherCause"
                      name="otherCause"
                      placeholder="Enter the cause"
                      type="text"
                      value={form.otherCause}
                      onChange={handleInputChange}
                    />
                    {errors.otherCause && <div className="advance-payment-error-text">{errors.otherCause}</div>}
                  </div>
                )}

                <div className="advance-payment-apply-fields">
                  <div className="advance-payment-field-client">
                    <label className="advance-payment-form-label" htmlFor="advanceClientName">
                      Client Name
                    </label>
                    <Select
                      inputId="advanceClientName"
                      className={errors.clientName ? "react-select is-invalid" : "react-select"}
                      classNamePrefix="react-select"
                      isClearable
                      isLoading={clientOptionsLoading}
                      options={advanceClientSelectOptions}
                      value={selectedAdvanceClient}
                      onChange={handleClientChange}
                      placeholder="Select client"
                      menuPortalTarget={selectMenuPortalTarget}
                      menuPlacement="top"
                      menuPosition="fixed"
                      styles={selectMenuPortalStyles}
                      noOptionsMessage={() => clientOptionsLoading ? "Loading clients..." : "No clients found"}
                    />
                    {errors.clientName && <div className="advance-payment-error-text">{errors.clientName}</div>}
                  </div>

                  <div className="advance-payment-field-location">
                    <label className="advance-payment-form-label" htmlFor="advanceLocation">
                      Location
                    </label>
                    <input
                      className="form-control"
                      id="advanceLocation"
                      name="location"
                      placeholder="Enter location"
                      type="text"
                      value={form.location}
                      onChange={handleInputChange}
                    />
                    {errors.location && <div className="advance-payment-error-text">{errors.location}</div>}
                  </div>

                  <div className="advance-payment-field-amount">
                    <label className="advance-payment-form-label" htmlFor="advanceAmount">
                      Amount
                    </label>
                    <input
                      className="form-control"
                      id="advanceAmount"
                      name="amount"
                      placeholder="Enter amount"
                      type="text"
                      inputMode="decimal"
                      value={form.amount}
                      onChange={handleInputChange}
                    />
                    {errors.amount && <div className="advance-payment-error-text">{errors.amount}</div>}
                  </div>
                </div>

                <div className="ap-reuse-card mt-3" style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: 14,
                  background: reuseEnabled ? "#f0f7ff" : "#fafbfc",
                }}>
                  <label className="advance-payment-check" style={{ cursor: "pointer", fontWeight: 600, color: "#111827" }}>
                    <input
                      type="checkbox"
                      checked={reuseEnabled}
                      onChange={(e) => {
                        setReuseEnabled(e.target.checked);
                        if (!e.target.checked) {
                          setReuseSource(null);
                          setReuseAmountInput("");
                        }
                      }}
                    />
                    <span><i className="ri-recycle-line me-1" /> Use leftover from a previous request</span>
                  </label>

                  {reuseEnabled ? (
                    <div className="mt-3">
                      {reusableLoading ? (
                        <div className="text-muted" style={{ fontSize: 13 }}>Loading available sources...</div>
                      ) : reusableSources.length === 0 ? (
                        <div style={{ fontSize: 13, color: "#dc2626" }}>
                          <i className="ri-information-line me-1" />
                          No paid requests with leftover available.
                        </div>
                      ) : (
                        <>
                          <label className="advance-payment-form-label">Source Request</label>
                          <Select
                            isClearable
                            isSearchable
                            placeholder="Select previous request"
                            classNamePrefix="react-select"
                            options={reusableSources.map((s) => ({
                              value: s.id,
                              label: `${s.request_code} (₹${Number(s.available).toLocaleString("en-IN")} leftover)`,
                              source: s,
                            }))}
                            value={reuseSource ? {
                              value: reuseSource.id,
                              label: `${reuseSource.request_code} (₹${Number(reuseSource.available).toLocaleString("en-IN")} leftover)`,
                              source: reuseSource,
                            } : null}
                            onChange={(opt) => {
                              setReuseSource(opt?.source || null);
                              setReuseAmountInput("");
                            }}
                            menuPortalTarget={selectMenuPortalTarget}
                            styles={selectMenuPortalStyles}
                          />
                          {reuseSource ? (
                            <div className="mt-2">
                              <label className="advance-payment-form-label">Amount to Reuse</label>
                              <input
                                type="text"
                                inputMode="decimal"
                                className="form-control"
                                placeholder={`Max ₹${Number(reuseSource.available).toLocaleString("en-IN")}`}
                                value={reuseAmountInput}
                                onChange={(e) => setReuseAmountInput(e.target.value)}
                              />
                              <small className="text-muted">
                                Paid ₹{Number(reuseSource.paid_amount).toLocaleString("en-IN")} · Spent ₹{Number(reuseSource.spent).toLocaleString("en-IN")} · Consumed ₹{Number(reuseSource.consumed).toLocaleString("en-IN")}
                              </small>
                            </div>
                          ) : null}
                        </>
                      )}

                      {reuseSource && Number(form.amount) > 0 && Number(reuseAmountInput) > 0 ? (
                        <div className="mt-3 p-3" style={{ background: "#fff", border: "1px solid #cfe2ff", borderRadius: 8 }}>
                          <div style={{ fontWeight: 600, color: "#0e479c", marginBottom: 6, fontSize: 13 }}>
                            <i className="ri-calculator-line me-1" /> Funding Breakdown
                          </div>
                          <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.8 }}>
                            <div className="d-flex justify-content-between">
                              <span>Requested Amount:</span>
                              <strong>₹{Number(parseAmountInput(form.amount) || 0).toLocaleString("en-IN")}</strong>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span>Reused from {reuseSource.request_code}:</span>
                              <strong style={{ color: "#15803d" }}>− ₹{Number(parseAmountInput(reuseAmountInput) || 0).toLocaleString("en-IN")}</strong>
                            </div>
                            <hr className="my-1" />
                            <div className="d-flex justify-content-between">
                              <span><strong>Fresh Ask (Accounts):</strong></span>
                              <strong style={{ color: "#0e479c", fontSize: 15 }}>
                                ₹{Number(Math.max(0, (parseAmountInput(form.amount) || 0) - (parseAmountInput(reuseAmountInput) || 0))).toLocaleString("en-IN")}
                              </strong>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <div className="ap-bank-card mt-3" style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: 14,
                  background: "#fafbfc",
                  position: "relative",
                }}>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <div style={{ fontWeight: 600, color: "#111827", fontSize: 14 }}>
                      <i className="ri-bank-line me-1" /> Your Bank Details
                    </div>
                    <button
                      type="button"
                      onClick={openBankModal}
                      className="btn btn-sm btn-light"
                      title={bankDetails ? "Edit bank details" : "Add bank details"}
                      style={{ padding: "2px 8px" }}
                    >
                      <i className="ri-pencil-line" />
                    </button>
                  </div>
                  {bankDetails && bankDetails.account_no ? (
                    <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.7 }}>
                      <div><strong>Holder:</strong> {bankDetails.holder_name}</div>
                      <div>
                        <strong>A/c:</strong> <span style={{ fontFamily: "monospace" }}>{(bankDetails.account_no || "").replace(/(.{4})/g, "$1 ").trim()}</span>
                        <span style={{ margin: "0 8px", color: "#9ca3af" }}>·</span>
                        <strong>IFSC:</strong> {bankDetails.ifsc}
                      </div>
                      <div>
                        <strong>Bank:</strong> {bankDetails.bank_name || "-"}
                        {bankDetails.branch_name ? <> — {bankDetails.branch_name}</> : null}
                      </div>
                      <div style={{ marginTop: 4 }}>
                        {bankDetails.verified_at ? (
                          <span className="badge bg-success-transparent">
                            <i className="ri-shield-check-line me-1" /> Verified
                          </span>
                        ) : (
                          <span className="badge bg-warning-transparent">
                            <i className="ri-error-warning-line me-1" /> Unverified
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 13, color: "#dc2626" }}>
                      <i className="ri-error-warning-line me-1" />
                      No bank details added yet. Click the pencil to add.
                    </div>
                  )}
                </div>

              </div>

              <div className="advance-payment-modal-footer">
                <button type="button" className="btn btn-light" onClick={resetAndCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="advance-payment-primary-btn" disabled={submitting}>
                  <i className="ri-check-line" />
                  {submitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {bankModal.open && (
        <div className="advance-payment-modal-backdrop" style={{ zIndex: 11100 }}>
          <div className="advance-payment-modal" role="dialog" aria-modal="true" style={{ maxWidth: 520 }}>
            <div className="advance-payment-modal-header">
              <div>
                <h3>Bank Details</h3>
                <p className="advance-payment-modal-subtitle">
                  Saved details will be used for all your advance payment transfers.
                </p>
              </div>
              <button type="button" onClick={closeBankModal} aria-label="Close">
                <i className="ri-close-line" />
              </button>
            </div>
            <form onSubmit={handleBankSubmit} noValidate>
              <div className="advance-payment-modal-body">
                <div className="mb-3">
                  <label className="advance-payment-form-label" htmlFor="bankHolderName">
                    Account Holder Name
                  </label>
                  <input
                    id="bankHolderName"
                    name="holderName"
                    type="text"
                    className="form-control"
                    placeholder="As per bank records"
                    value={bankModal.form.holderName}
                    onChange={(e) => setBankModal((prev) => ({
                      ...prev,
                      form: { ...prev.form, holderName: e.target.value },
                      errors: { ...prev.errors, holderName: "" },
                    }))}
                  />
                  {bankModal.errors.holderName && (
                    <div className="advance-payment-error-text">{bankModal.errors.holderName}</div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="advance-payment-form-label" htmlFor="bankAccountNo">
                    Account Number
                  </label>
                  <input
                    id="bankAccountNo"
                    name="accountNo"
                    type="text"
                    inputMode="numeric"
                    className="form-control"
                    placeholder="Enter account number"
                    value={bankModal.form.accountNo}
                    onChange={handleBankInputChange}
                  />
                  {bankModal.errors.accountNo && (
                    <div className="advance-payment-error-text">{bankModal.errors.accountNo}</div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="advance-payment-form-label" htmlFor="bankConfirmAccountNo">
                    Re-enter Account Number
                  </label>
                  <input
                    id="bankConfirmAccountNo"
                    name="confirmAccountNo"
                    type="text"
                    inputMode="numeric"
                    className="form-control"
                    placeholder="Re-enter account number"
                    value={bankModal.form.confirmAccountNo}
                    onChange={handleBankInputChange}
                    onPaste={(e) => e.preventDefault()}
                  />
                  {bankModal.errors.confirmAccountNo && (
                    <div className="advance-payment-error-text">{bankModal.errors.confirmAccountNo}</div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="advance-payment-form-label" htmlFor="bankIfsc">
                    IFSC Code
                  </label>
                  <input
                    id="bankIfsc"
                    name="ifsc"
                    type="text"
                    className="form-control"
                    placeholder="e.g. HDFC0001234"
                    maxLength={11}
                    style={{ textTransform: "uppercase" }}
                    value={bankModal.form.ifsc}
                    onChange={handleBankInputChange}
                  />
                  {bankModal.errors.ifsc && (
                    <div className="advance-payment-error-text">{bankModal.errors.ifsc}</div>
                  )}
                  <small className="text-muted">Bank and branch will be fetched automatically on verification.</small>
                </div>

                {bankDetails && bankDetails.bank_name ? (
                  <div className="mb-1" style={{
                    fontSize: 12,
                    background: "#eef6ff",
                    border: "1px solid #cfe2ff",
                    padding: "8px 10px",
                    borderRadius: 8,
                    color: "#0e479c",
                  }}>
                    Last verified: <strong>{bankDetails.bank_name}</strong>
                    {bankDetails.branch_name ? <> — {bankDetails.branch_name}</> : null}
                  </div>
                ) : null}
              </div>
              <div className="advance-payment-modal-footer">
                <button type="button" className="btn btn-light" onClick={closeBankModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="advance-payment-primary-btn"
                  disabled={bankModal.submitting}
                >
                  <i className="ri-shield-check-line" />
                  {bankModal.submitting ? "Verifying..." : "Verify & Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {downloadModal.open && (
        <div className="advance-payment-modal-backdrop">
          <div className="advance-payment-modal" role="dialog" aria-modal="true" style={{ maxWidth: 560 }}>
            <div className="advance-payment-modal-header">
              <div>
                <h3>{downloadModal.section === "apply" ? "Download Request Summary" : "Download Expense Summary"}</h3>
                <p className="advance-payment-modal-subtitle">
                  {downloadModal.section === "apply"
                    ? "Export request list as CSV with status, bank and UTR details."
                    : "Export expense entries as CSV for the selected requesters and date range."}
                </p>
              </div>
              <button type="button" onClick={() => setDownloadModal((prev) => ({ ...prev, open: false }))} aria-label="Close">
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="advance-payment-modal-body">
              <div className="mb-3">
                <div className="advance-payment-form-label mb-2">Requester</div>
                <div className="d-flex gap-3 mb-2">
                  <label className="advance-payment-check" style={{ cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="requesterMode"
                      checked={downloadModal.requesterMode === "all"}
                      onChange={() => setDownloadModal((prev) => ({ ...prev, requesterMode: "all", requesterIds: [] }))}
                    />
                    <span>All Requesters</span>
                  </label>
                  <label className="advance-payment-check" style={{ cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="requesterMode"
                      checked={downloadModal.requesterMode === "specific"}
                      onChange={() => setDownloadModal((prev) => ({ ...prev, requesterMode: "specific" }))}
                    />
                    <span>Specific Requesters</span>
                  </label>
                </div>
                {downloadModal.requesterMode === "specific" ? (
                  <Select
                    isMulti
                    isClearable
                    isSearchable
                    placeholder="Select requesters..."
                    classNamePrefix="react-select"
                    options={requesterOptions.map((r) => ({ value: r.id, label: r.name + (r.post ? ` (${r.post.replace(/_/g, " ")})` : "") }))}
                    value={downloadModal.requesterIds
                      .map((id) => {
                        const found = requesterOptions.find((r) => r.id === id);
                        return found ? { value: found.id, label: found.name + (found.post ? ` (${found.post.replace(/_/g, " ")})` : "") } : null;
                      })
                      .filter(Boolean)}
                    onChange={(opts) => setDownloadModal((prev) => ({ ...prev, requesterIds: (opts || []).map((o) => o.value) }))}
                    menuPortalTarget={selectMenuPortalTarget}
                    styles={selectMenuPortalStyles}
                  />
                ) : null}
              </div>

              <div className="row g-2 mb-2">
                <div className="col-6">
                  <label className="advance-payment-form-label" htmlFor="dlFromDate">From Date</label>
                  <input
                    id="dlFromDate"
                    type="date"
                    className="form-control"
                    value={downloadModal.fromDate}
                    onChange={(e) => setDownloadModal((prev) => ({ ...prev, fromDate: e.target.value }))}
                  />
                </div>
                <div className="col-6">
                  <label className="advance-payment-form-label" htmlFor="dlToDate">To Date</label>
                  <input
                    id="dlToDate"
                    type="date"
                    className="form-control"
                    value={downloadModal.toDate}
                    onChange={(e) => setDownloadModal((prev) => ({ ...prev, toDate: e.target.value }))}
                  />
                </div>
              </div>
              <small className="text-muted">
                {downloadModal.section === "apply"
                  ? "Date range filters by request creation date. Leave empty to include all requests."
                  : "Date range filters by expense entry date. Leave empty to include all expenses."}
              </small>
            </div>
            <div className="advance-payment-modal-footer">
              <button type="button" className="btn btn-light" onClick={() => setDownloadModal((prev) => ({ ...prev, open: false }))}>
                Cancel
              </button>
              <button
                type="button"
                className="advance-payment-primary-btn"
                disabled={downloadModal.submitting || (downloadModal.requesterMode === "specific" && downloadModal.requesterIds.length === 0)}
                onClick={async () => {
                  if (downloadModal.fromDate && downloadModal.toDate && downloadModal.fromDate > downloadModal.toDate) {
                    Swal.fire("Invalid range", "From date cannot be after To date.", "warning");
                    return;
                  }
                  const endpoint = downloadModal.section === "apply" ? "requests_csv" : "expenses_csv";
                  const params = { fetch: endpoint };
                  if (downloadModal.requesterMode === "specific" && downloadModal.requesterIds.length > 0) {
                    params.requester_filter = downloadModal.requesterIds.join(",");
                  }
                  if (downloadModal.fromDate) params.from_date = downloadModal.fromDate;
                  if (downloadModal.toDate) params.to_date = downloadModal.toDate;
                  try {
                    setDownloadModal((prev) => ({ ...prev, submitting: true }));
                    const response = await axios.get(`${apiurl}advance_payment.php`, { params, responseType: "blob" });
                    const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    const datePart = (downloadModal.fromDate || "start") + "_to_" + (downloadModal.toDate || "end");
                    const prefix = downloadModal.section === "apply" ? "advance_requests" : "advance_expenses";
                    a.download = `${prefix}_${datePart}.csv`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                    setDownloadModal({ open: false, section: "expenses", requesterMode: "all", requesterIds: [], fromDate: "", toDate: "", submitting: false });
                  } catch (err) {
                    setDownloadModal((prev) => ({ ...prev, submitting: false }));
                    Swal.fire("Download failed", "Could not generate CSV. Please try again.", "error");
                  }
                }}
              >
                <i className="ri-download-2-line" />
                {downloadModal.submitting ? "Downloading..." : "Download CSV"}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedRequest && (
        <div className="advance-payment-modal-backdrop">
          <div className="advance-payment-modal advance-payment-details-modal" role="dialog" aria-modal="true">
            <div className="advance-payment-modal-header">
              <h3>Request Details</h3>
              <button type="button" onClick={() => setSelectedRequest(null)} aria-label="Close">
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="advance-payment-modal-body">
              <div className="advance-payment-details-hero">
                <div className="advance-payment-details-hero-main">
                  <span className="advance-payment-details-eyebrow">Advance Request</span>
                  <h4>{selectedRequest.request_code || "-"}</h4>
                  <p>
                    {selectedRequest.client_name || "-"} | {formatAmount(selectedRequest.amount)}
                  </p>
                </div>
                <div className="advance-payment-details-hero-badges">
                  {renderStatusBadge(
                    stageLabels[selectedRequest.current_stage] || "Completed",
                    "primary"
                  )}
                  {renderStatusBadge(
                    selectedRequest.overall_status === "paid"
                      ? "Paid"
                      : selectedRequest.overall_status === "rejected"
                        ? "Rejected"
                        : "In Progress",
                    selectedRequest.overall_status === "paid"
                      ? "success"
                      : selectedRequest.overall_status === "rejected"
                        ? "danger"
                        : "warning"
                  )}
                </div>
              </div>

              <div className="advance-payment-detail-grid advance-payment-details-grid">
                <div className="advance-payment-detail-panel">
                  <div className="advance-payment-panel-title">Request Info</div>
                  <div className="advance-payment-detail-list advance-payment-detail-list-inline">
                    <div><strong>Request Code :</strong> {selectedRequest.request_code || "-"}</div>
                    <div><strong>Requested On :</strong> {formatDateTime(selectedRequest.created_at)}</div>
                    <div><strong>Client Name :</strong> {selectedRequest.client_name || "-"}</div>
                    <div><strong>Location :</strong> {selectedRequest.location_name || "-"}</div>
                    <div><strong>Amount :</strong> {formatAmount(selectedRequest.amount)}</div>
                    {selectedRequest.reuse_info?.is_reuse ? (
                      <>
                        <div><strong>Reused From :</strong> {selectedRequest.reuse_info.reused_from_request_code}</div>
                        <div><strong>Reused Amount :</strong> {formatAmount(selectedRequest.reuse_info.reused_amount)}</div>
                        <div><strong>Fresh Ask :</strong> {formatAmount(selectedRequest.reuse_info.effective_ask_amount)}</div>
                      </>
                    ) : null}
                    {selectedRequest.leftover_info ? (
                      <div><strong>Leftover Available :</strong> {formatAmount(selectedRequest.leftover_info.available)}
                        {Number(selectedRequest.leftover_info.consumed) > 0 ? (
                          <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 6 }}>
                            (₹{Number(selectedRequest.leftover_info.consumed).toLocaleString("en-IN")} already reused elsewhere)
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                    <div><strong>Purpose :</strong> {(selectedRequest.purposes || []).join(", ") || "-"}</div>
                    {selectedRequest.other_cause ? (
                      <div><strong>Other Cause :</strong> {selectedRequest.other_cause}</div>
                    ) : null}
                  </div>
                </div>

                <div className="advance-payment-detail-panel">
                  <div className="advance-payment-panel-title">People</div>
                  <div className="advance-payment-detail-list advance-payment-detail-list-inline">
                    <div><strong>Requester :</strong> {selectedRequest.created_by_name || "-"}</div>
                    <div><strong>Designation :</strong> {(selectedRequest.created_by_post || "").replace(/_/g, " ") || "-"}</div>
                    <div><strong>Current Stage :</strong> {stageLabels[selectedRequest.current_stage] || "Completed"}</div>
                  </div>
                </div>

                <div className="advance-payment-detail-panel">
                  <div className="advance-payment-panel-title">Payment Status</div>
                  <div className="advance-payment-detail-list advance-payment-detail-list-inline">
                    <div><strong>UTR :</strong> {selectedRequest.utr_no || "-"}</div>
                    <div><strong>Accounts Status :</strong> {normalizeStateLabel(selectedRequest.accounts_status)}</div>
                    <div><strong>Paid At :</strong> {formatDateTime(selectedRequest.payment_marked_at)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {statusModal.open && statusModal.request && (() => {
        const steps = buildStatusSteps(statusModal.request);
        const overall = statusModal.request.overall_status || "pending";
        const overallLabel = overall === "paid" ? "Paid" : overall === "rejected" ? "Rejected" : "In Progress";
        const overallType = overall === "paid" ? "success" : overall === "rejected" ? "danger" : "warning";
        return (
          <div className="advance-payment-modal-backdrop">
            <div className="advance-payment-modal advance-payment-status-modal" role="dialog" aria-modal="true">
              <div className="advance-payment-modal-header">
                <div>
                  <h3>Request Status</h3>
                  <p className="advance-payment-modal-subtitle">
                    {statusModal.request.request_code || "-"} | {statusModal.request.created_by_name || "-"} | {formatAmount(statusModal.request.amount)}
                  </p>
                </div>
                <button type="button" onClick={() => setStatusModal(initialStatusModal)} aria-label="Close">
                  <i className="ri-close-line" />
                </button>
              </div>
              <div className="advance-payment-modal-body">
                <div className="advance-payment-status-overall">
                  {renderStatusBadge(overallLabel, overallType)}
                  {renderStatusBadge(stageLabels[statusModal.request.current_stage] || "Completed", "primary")}
                </div>
                {statusModal.request.reuse_info?.is_reuse ? (
                  <div style={{ background: "#f0f7ff", border: "1px solid #cfe2ff", borderRadius: 8, padding: "10px 12px", margin: "8px 0 12px", fontSize: 13, color: "#374151" }}>
                    <div style={{ fontWeight: 600, color: "#0e479c", marginBottom: 4 }}>
                      <i className="ri-recycle-line me-1" /> Reused from {statusModal.request.reuse_info.reused_from_request_code}
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>Reused Amount:</span>
                      <strong>{formatAmount(statusModal.request.reuse_info.reused_amount)}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>Fresh Ask:</span>
                      <strong style={{ color: "#0e479c" }}>{formatAmount(statusModal.request.reuse_info.effective_ask_amount)}</strong>
                    </div>
                  </div>
                ) : null}
                <div className="advance-payment-status-stepper">
                  {steps.map((step, idx) => {
                    const isLast = idx === steps.length - 1;
                    const connectorClass =
                      step.icon === "done" || step.icon === "progress"
                        ? "advance-payment-status-connector done"
                        : "advance-payment-status-connector";
                    return (
                      <div className="advance-payment-status-step" key={`${step.title}-${idx}`}>
                        <div className="advance-payment-status-step-top">
                          <div className={`advance-payment-status-circle ${step.icon}`}>
                            {step.icon === "done" ? (
                              <i className="ri-check-line" />
                            ) : step.icon === "rejected" ? (
                              <i className="ri-close-line" />
                            ) : step.icon === "pending" ? (
                              <i className="ri-time-line" />
                            ) : step.icon === "progress" ? (
                              <i className="ri-loader-4-line" />
                            ) : (
                              <span>{idx + 1}</span>
                            )}
                          </div>
                          {!isLast && <div className={connectorClass} />}
                        </div>
                        <div className="advance-payment-status-step-body">
                          <div className="advance-payment-status-step-title">{step.title}</div>
                          {step.actorName ? (
                            <>
                              <div className="advance-payment-status-step-actor">{step.actorName}</div>
                              {step.actorPost ? (
                                <div className="advance-payment-status-step-post">{step.actorPost}</div>
                              ) : null}
                              {step.actorAt ? (
                                <div className="advance-payment-status-step-time">{formatDateTime(step.actorAt)}</div>
                              ) : null}
                              {step.extra ? (
                                <div className="advance-payment-status-step-extra">{step.extra}</div>
                              ) : null}
                              {step.pendingNote ? (
                                <div className="advance-payment-status-step-pending">{step.pendingNote}</div>
                              ) : null}
                            </>
                          ) : step.pendingWith ? (
                            <>
                              <div className="advance-payment-status-step-pending">Pending with</div>
                              <div className="advance-payment-status-step-actor">{step.pendingWith}</div>
                            </>
                          ) : (
                            <div className="advance-payment-status-step-pending">Not started</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {decisionModal.open && decisionModal.request && (
        <div className="advance-payment-modal-backdrop">
          <div className="advance-payment-modal advance-payment-decision-modal" role="dialog" aria-modal="true">
            <div className={`advance-payment-modal-header ${decisionModal.action === "reject" ? "danger" : "success"}`}>
              <div>
                <h3>{decisionModal.action === "reject" ? "Reject Advance Request" : "Approve Advance Request"}</h3>
                <p className="advance-payment-modal-subtitle">
                  Add your note and continue.
                </p>
              </div>
              <button type="button" onClick={closeDecisionModal} aria-label="Close">
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="advance-payment-modal-body">
              <div className={`advance-payment-decision-hero ${decisionModal.action === "reject" ? "danger" : "success"}`}>
                <div className="advance-payment-decision-hero-icon">
                  <i className={decisionModal.action === "reject" ? "ri-close-circle-line" : "ri-checkbox-circle-line"} />
                </div>
                <div className="advance-payment-decision-hero-copy">
                  <h4>{decisionModal.request.request_code || "-"}</h4>
                  <p>
                    {decisionModal.request.created_by_name || "-"} | {decisionModal.request.client_name || "-"} | {formatAmount(decisionModal.request.amount)}
                  </p>
                </div>
                <div className="advance-payment-decision-hero-badges">
                  {renderStatusBadge(stageLabels[decisionModal.request.current_stage] || "Completed", "primary")}
                  {renderStatusBadge(
                    decisionModal.action === "reject" ? "Rejecting" : "Approving",
                    decisionModal.action === "reject" ? "danger" : "success"
                  )}
                </div>
              </div>

              {decisionModal.request.reuse_info?.is_reuse ? (
                <div style={{ background: "#f0f7ff", border: "1px solid #cfe2ff", borderRadius: 8, padding: "12px 14px", marginTop: 12, marginBottom: 16, fontSize: 13, color: "#374151" }}>
                  <div style={{ fontWeight: 600, color: "#0e479c", marginBottom: 6 }}>
                    <i className="ri-recycle-line me-1" /> Funding Breakdown
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Requested Amount:</span>
                    <strong>{formatAmount(decisionModal.request.amount)}</strong>
                  </div>
                  <div className="d-flex justify-content-between" style={{ color: "#15803d" }}>
                    <span>Reused from {decisionModal.request.reuse_info.reused_from_request_code}:</span>
                    <strong>− {formatAmount(decisionModal.request.reuse_info.reused_amount)}</strong>
                  </div>
                  <hr className="my-1" />
                  <div className="d-flex justify-content-between" style={{ fontSize: 15 }}>
                    <span><strong>Fresh Ask (Accounts):</strong></span>
                    <strong style={{ color: "#0e479c" }}>{formatAmount(decisionModal.request.reuse_info.effective_ask_amount)}</strong>
                  </div>
                </div>
              ) : null}

              <div className="advance-payment-decision-meta">
                <div><span>Location</span><strong>{decisionModal.request.location_name || "-"}</strong></div>
                <div><span>Purpose</span><strong>{(decisionModal.request.purposes || []).join(", ") || "-"}</strong></div>
                <div><span>Requested On</span><strong>{formatDateTime(decisionModal.request.created_at)}</strong></div>
                <div><span>Role</span><strong>{(decisionModal.request.created_by_post || "").replace(/_/g, " ") || "-"}</strong></div>
                {decisionModal.request.other_cause ? (
                  <div className="full"><span>Other Cause</span><strong>{decisionModal.request.other_cause}</strong></div>
                ) : null}
                </div>

              <div className="advance-payment-decision-note">
                <label className="advance-payment-form-label" htmlFor="advanceDecisionRemark">
                  {decisionModal.action === "reject" ? "Rejection Remark" : "Approval Remark"}
                </label>
                <textarea
                  id="advanceDecisionRemark"
                  className="form-control advance-payment-textarea"
                  rows="3"
                  placeholder={
                    decisionModal.action === "reject"
                      ? "Explain why this request is being rejected"
                      : "Add an optional note for the next team member"
                  }
                  value={decisionModal.remark}
                  onChange={handleDecisionRemarkChange}
                />
                {decisionModal.error ? (
                  <div className="advance-payment-error-text">{decisionModal.error}</div>
                ) : null}
              </div>
            </div>

            <div className="advance-payment-modal-footer">
              <button type="button" className="btn btn-light" onClick={closeDecisionModal}>
                Cancel
              </button>
              <button
                type="button"
                className={decisionModal.action === "reject" ? "advance-payment-secondary-btn danger" : "advance-payment-primary-btn"}
                onClick={handleApprovalAction}
                disabled={busyByRequest[decisionModal.request.id] === decisionModal.action}
              >
                <i className={decisionModal.action === "reject" ? "ri-close-line" : "ri-check-line"} />
                {busyByRequest[decisionModal.request.id] === decisionModal.action
                  ? decisionModal.action === "reject"
                    ? "Rejecting..."
                    : "Approving..."
                  : decisionModal.action === "reject"
                    ? "Confirm Reject"
                    : "Confirm Approve"}
              </button>
            </div>
          </div>
        </div>
      )}

      {spocModal.open && spocModal.request && (
        <div className="advance-payment-modal-backdrop">
          <div className="advance-payment-modal advance-payment-decision-modal" role="dialog" aria-modal="true">
            <div className="advance-payment-modal-header success">
              <div>
                <h3>Update SPOC Details</h3>
                <p className="advance-payment-modal-subtitle">
                  Add transfer details after the payment has been completed.
                </p>
              </div>
              <button type="button" onClick={closeSpocModal} aria-label="Close">
                <i className="ri-close-line" />
              </button>
            </div>

            <form onSubmit={handleSpocSubmit} noValidate>
              <div className="advance-payment-modal-body">
                <div className="advance-payment-decision-hero success">
                  <div className="advance-payment-decision-hero-icon">
                    <i className="ri-user-settings-line" />
                  </div>
                  <div className="advance-payment-decision-hero-copy">
                    <h4>{spocModal.request.request_code || "-"}</h4>
                    <p>
                      {spocModal.request.client_name || "-"} | {formatAmount(spocModal.request.amount)}
                    </p>
                  </div>
                  <div className="advance-payment-decision-hero-badges">
                    {renderStatusBadge("Paid", "success")}
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="advance-payment-form-label" htmlFor="advanceSpocName">
                      SPOC Name
                    </label>
                    <input
                      id="advanceSpocName"
                      name="spocName"
                      type="text"
                      className="form-control"
                      placeholder="Enter SPOC name"
                      value={spocModal.form.spocName}
                      onChange={handleSpocFieldChange}
                    />
                    {spocModal.errors.spocName ? <div className="advance-payment-error-text">{spocModal.errors.spocName}</div> : null}
                  </div>

                  <div className="col-md-6">
                    <label className="advance-payment-form-label" htmlFor="advanceSpocMobile">
                      Mobile Number
                    </label>
                    <input
                      id="advanceSpocMobile"
                      name="spocMobile"
                      type="tel"
                      className="form-control"
                      placeholder="Enter mobile number"
                      value={spocModal.form.spocMobile}
                      onChange={handleSpocFieldChange}
                      inputMode="numeric"
                      maxLength="10"
                    />
                    {spocModal.errors.spocMobile ? <div className="advance-payment-error-text">{spocModal.errors.spocMobile}</div> : null}
                  </div>

                  <div className="col-md-6">
                    <label className="advance-payment-form-label" htmlFor="advanceSpocEmail">
                      Email ID
                    </label>
                    <input
                      id="advanceSpocEmail"
                      name="spocEmail"
                      type="email"
                      className="form-control"
                      placeholder="Enter email ID"
                      value={spocModal.form.spocEmail}
                      onChange={handleSpocFieldChange}
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                    {spocModal.errors.spocEmail ? <div className="advance-payment-error-text">{spocModal.errors.spocEmail}</div> : null}
                  </div>

                  <div className="col-md-6">
                    <label className="advance-payment-form-label" htmlFor="advanceTransferredAmount">
                      Amount Transferred
                    </label>
                    <input
                      id="advanceTransferredAmount"
                      name="amountTransferred"
                      type="text"
                      inputMode="decimal"
                      className="form-control"
                      placeholder="Enter transferred amount"
                      value={spocModal.form.amountTransferred}
                      onChange={handleSpocFieldChange}
                    />
                    {spocModal.errors.amountTransferred ? <div className="advance-payment-error-text">{spocModal.errors.amountTransferred}</div> : null}
                  </div>

                  <div className="col-md-6">
                    <label className="advance-payment-form-label" htmlFor="advanceTransferredDate">
                      Transferred Date
                    </label>
                    <input
                      id="advanceTransferredDate"
                      name="transferredDate"
                      type="date"
                      className="form-control"
                      value={spocModal.form.transferredDate}
                      onChange={handleSpocFieldChange}
                    />
                    {spocModal.errors.transferredDate ? <div className="advance-payment-error-text">{spocModal.errors.transferredDate}</div> : null}
                  </div>

                  <div className="col-12">
                    <label className="advance-payment-form-label" htmlFor="advanceTransferCause">
                      Cause for Transfer
                    </label>
                    <textarea
                      id="advanceTransferCause"
                      name="transferCause"
                      className="form-control advance-payment-textarea"
                      rows="3"
                      placeholder="Enter the cause for transfer"
                      value={spocModal.form.transferCause}
                      onChange={handleSpocFieldChange}
                    />
                    {spocModal.errors.transferCause ? <div className="advance-payment-error-text">{spocModal.errors.transferCause}</div> : null}
                  </div>
                </div>
              </div>

              <div className="advance-payment-modal-footer">
                <button type="button" className="btn btn-light" onClick={closeSpocModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="advance-payment-primary-btn"
                  disabled={busyByRequest[spocModal.request.id] === "update_spoc"}
                >
                  <i className="ri-check-line" />
                  {busyByRequest[spocModal.request.id] === "update_spoc" ? "Updating..." : "Save SPOC Details"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {spocViewModal.open && spocViewModal.request && (
        <div className="advance-payment-modal-backdrop">
          <div className="advance-payment-modal advance-payment-decision-modal" role="dialog" aria-modal="true">
            <div className="advance-payment-modal-header">
              <h3>SPOC Details</h3>
              <button type="button" onClick={closeSpocViewModal} aria-label="Close">
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="advance-payment-modal-body">
              <div className="advance-payment-decision-hero success">
                <div className="advance-payment-decision-hero-icon">
                  <i className="ri-user-settings-line" />
                </div>
                <div className="advance-payment-decision-hero-copy">
                  <h4>{spocViewModal.request.request_code || "-"}</h4>
                  <p>
                    {spocViewModal.request.client_name || "-"} | {formatAmount(spocViewModal.request.amount)}
                  </p>
                </div>
              </div>

              <div className="advance-payment-detail-panel">
                <div className="advance-payment-panel-title">SPOC Transfer Details</div>
                <div className="advance-payment-detail-list advance-payment-detail-list-inline">
                  <div><strong>SPOC Name :</strong> {spocViewModal.request.spoc_name || "-"}</div>
                  <div><strong>Mobile Number :</strong> {spocViewModal.request.spoc_mobile || "-"}</div>
                  <div><strong>Email ID :</strong> {spocViewModal.request.spoc_email || "-"}</div>
                  <div><strong>Amount Transferred :</strong> {spocViewModal.request.spoc_amount_transferred ? formatAmount(spocViewModal.request.spoc_amount_transferred) : "-"}</div>
                  <div><strong>Transferred Date :</strong> {spocViewModal.request.spoc_transferred_date ? formatDateTime(spocViewModal.request.spoc_transferred_date) : "-"}</div>
                  <div><strong>Cause for Transfer :</strong> {spocViewModal.request.spoc_transfer_cause || "-"}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {expensesModal.open && expensesModal.request && (
        <div className="advance-payment-modal-backdrop">
          <div className="advance-payment-modal advance-payment-expenses-modal" role="dialog" aria-modal="true">
            <div className="advance-payment-modal-header">
              <div>
                <h3>Update Expenses</h3>
                <p className="advance-payment-modal-subtitle">
                  Add one or more spend rows for this paid advance request.
                </p>
              </div>
              <button type="button" onClick={closeExpensesModal} aria-label="Close">
                <i className="ri-close-line" />
              </button>
            </div>

            <form onSubmit={handleExpensesSubmit} noValidate>
              <div className="advance-payment-modal-body">
                {(() => {
                  const totalEntries = expensesModal.entries.length;
                  const currentPage = expensesModal.page || 1;
                  const startIndex = (currentPage - 1) * EXPENSES_PAGE_SIZE;
                  const pagedEntries = expensesModal.entries.slice(startIndex, startIndex + EXPENSES_PAGE_SIZE);

                  return (
                    <>
                <div className="advance-payment-decision-hero success">
                  <div className="advance-payment-decision-hero-icon">
                    <i className="ri-line-chart-line" />
                  </div>
                  <div className="advance-payment-decision-hero-copy">
                    <h4>{expensesModal.request.request_code || "-"}</h4>
                    <p>
                      {expensesModal.request.client_name || "-"} | {formatAmount(expensesModal.request.amount)}
                    </p>
                  </div>
                </div>

                {expensesModal.loading ? (
                  <div className="advance-payment-loading-block">Loading expenses rows...</div>
                ) : (
                  <>

                <div className="advance-payment-expense-list">
                  {pagedEntries.map((entry, pageIndex) => {
                    const index = startIndex + pageIndex;
                    const rowErrors = expensesModal.errors[index] || {};

                    return (
                      <div className="advance-payment-expense-card" key={`expense-entry-${index}`}>
                        <div className="advance-payment-expense-head">
                          <strong>Expense Row {index + 1}</strong>
                          {expensesModal.entries.length > 1 ? (
                            <button
                              type="button"
                              className="advance-payment-link-btn"
                              onClick={() => removeExpenseEntry(index)}
                            >
                              Remove
                            </button>
                          ) : null}
                        </div>

                        <div className="row g-3">
                          <div className="col-md-4">
                            <label className="advance-payment-form-label">Spent On</label>
                            <Select
                              options={spentOnOptions}
                              value={spentOnOptions.find((option) => option.value === entry.spentOn) || spentOnOptions[0]}
                              onChange={(option) => handleExpenseSpentOnChange(index, option.value)}
                              classNamePrefix="react-select"
                              isSearchable={false}
                            />
                          </div>

                          <div className="col-md-4">
                            <label className="advance-payment-form-label">Purpose</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Travel / Food / Medical"
                              value={entry.purpose}
                              onChange={(event) => handleExpenseEntryChange(index, "purpose", event.target.value)}
                            />
                            {rowErrors.purpose ? <div className="advance-payment-error-text">{rowErrors.purpose}</div> : null}
                          </div>

                          <div className="col-md-4">
                            <label className="advance-payment-form-label">Amount Spent</label>
                            <input
                              type="text"
                              inputMode="decimal"
                              className="form-control"
                              placeholder="Enter spent amount"
                              value={entry.amountSpent}
                              onChange={(event) => handleExpenseEntryChange(index, "amountSpent", event.target.value)}
                            />
                            {rowErrors.amountSpent ? <div className="advance-payment-error-text">{rowErrors.amountSpent}</div> : null}
                          </div>

                          <div className="col-md-4">
                            <label className="advance-payment-form-label">Expense Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={entry.expenseDate}
                              onChange={(event) => handleExpenseEntryChange(index, "expenseDate", event.target.value)}
                            />
                            {rowErrors.expenseDate ? <div className="advance-payment-error-text">{rowErrors.expenseDate}</div> : null}
                          </div>

                          <div className="col-md-4">
                            <label className="advance-payment-form-label">Candidate Name</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter candidate name"
                              value={entry.candidateName}
                              disabled={entry.spentOn === "self"}
                              onChange={(event) => handleExpenseEntryChange(index, "candidateName", event.target.value)}
                            />
                            {rowErrors.candidateName ? <div className="advance-payment-error-text">{rowErrors.candidateName}</div> : null}
                          </div>

                          <div className="col-md-4">
                            <label className="advance-payment-form-label">Mobile Number</label>
                            <input
                              type="tel"
                              inputMode="numeric"
                              maxLength="10"
                              className="form-control"
                              placeholder="Enter mobile number"
                              value={entry.mobileNumber}
                              disabled={entry.spentOn === "self"}
                              onChange={(event) => handleExpenseEntryChange(index, "mobileNumber", event.target.value)}
                            />
                            {rowErrors.mobileNumber ? <div className="advance-payment-error-text">{rowErrors.mobileNumber}</div> : null}
                          </div>

                          <div className="col-md-4">
                            <label className="advance-payment-form-label">Aadhaar Number</label>
                            <input
                              type="text"
                              inputMode="numeric"
                              maxLength="12"
                              className="form-control"
                              placeholder="Enter Aadhaar number"
                              value={entry.aadhaarNumber}
                              onChange={(event) => handleExpenseEntryChange(index, "aadhaarNumber", event.target.value)}
                            />
                            {rowErrors.aadhaarNumber ? <div className="advance-payment-error-text">{rowErrors.aadhaarNumber}</div> : null}
                          </div>

                          <div className="col-md-8">
                            <label className="advance-payment-form-label">Reason</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter reason"
                              value={entry.reason}
                              onChange={(event) => handleExpenseEntryChange(index, "reason", event.target.value)}
                            />
                            {rowErrors.reason ? <div className="advance-payment-error-text">{rowErrors.reason}</div> : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="advance-payment-expense-actions">
                  <button type="button" className="advance-payment-secondary-btn" onClick={addExpenseEntry}>
                    <i className="ri-add-line" />
                    Add Row
                  </button>
                </div>
                {renderExpensesPagination(currentPage, totalEntries, updateExpensesModalPage)}
                  </>
                )}
                    </>
                  );
                })()}
              </div>

              <div className="advance-payment-modal-footer">
                <button type="button" className="btn btn-light" onClick={closeExpensesModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="advance-payment-primary-btn"
                  disabled={busyByRequest[expensesModal.request.id] === "update_expenses"}
                >
                  <i className="ri-check-line" />
                  {busyByRequest[expensesModal.request.id] === "update_expenses" ? "Saving..." : "Save Expenses"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {expensesViewModal.open && expensesViewModal.request && (
        <div className="advance-payment-modal-backdrop">
          <div className="advance-payment-modal advance-payment-expenses-modal" role="dialog" aria-modal="true">
            <div className="advance-payment-modal-header">
              <div>
                <h3>Expenses Details</h3>
                <p className="advance-payment-modal-subtitle">
                  Review the submitted spend rows for this paid advance request.
                </p>
              </div>
              <button type="button" onClick={closeExpensesViewModal} aria-label="Close">
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="advance-payment-modal-body">
              {(() => {
                const currentPage = expensesViewModal.page || 1;
                const startIndex = (currentPage - 1) * EXPENSES_PAGE_SIZE;
                const totalEntries = Number(expensesViewModal.totalEntries || 0);
                const pagedEntries = expensesViewModal.entries || [];

                return (
                  <>
              <div className="advance-payment-details-hero">
                <div className="advance-payment-details-hero-main">
                  <span className="advance-payment-details-eyebrow">Paid Request</span>
                  <h4>{expensesViewModal.request.request_code || "-"}</h4>
                  <p>
                    {expensesViewModal.request.client_name || "-"} | {formatAmount(expensesViewModal.request.amount)}
                  </p>
                </div>
                <div className="advance-payment-details-hero-badges">
                  {renderStatusBadge("Paid", "success")}
                  {renderStatusBadge(
                    `${Number(expensesViewModal.request.expenses_summary?.entries_count || 0)} Entries`,
                    "primary"
                  )}
                </div>
              </div>

              <div className="advance-payment-expense-summary">
                <div className="advance-payment-summary-stats advance-payment-summary-stats-spaced">
                  <div className="advance-payment-summary-stat">
                    <span className="advance-payment-summary-label">Requester</span>
                    <strong>{expensesViewModal.request.created_by_name || "-"}</strong>
                  </div>
                  <div className="advance-payment-summary-stat">
                    <span className="advance-payment-summary-label">Total Spent</span>
                    <strong>{formatAmount(expensesViewModal.request.expenses_summary?.total_spent || 0)}</strong>
                  </div>
                  <div className="advance-payment-summary-stat">
                    <span className="advance-payment-summary-label">Rows Added</span>
                    <strong>{expensesViewModal.request.expenses_summary?.entries_count || 0}</strong>
                  </div>
                </div>
              </div>

              {expensesViewModal.loading ? (
                <div className="advance-payment-loading-block">Loading expenses rows...</div>
              ) : (
                <>

              <div className="table-responsive">
                <table className="table table-bordered table-hover align-middle mb-0" style={{ fontSize: 13 }}>
                  <thead style={{ background: "#f5f8fc" }}>
                    <tr>
                      <th style={{ width: 60 }}>#</th>
                      <th>Spent On</th>
                      <th>Purpose</th>
                      <th>Date</th>
                      <th>Candidate</th>
                      <th>Mobile</th>
                      <th>Aadhaar</th>
                      <th className="text-end">Amount</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedEntries.length > 0 ? pagedEntries.map((entry, pageIndex) => {
                      const index = startIndex + pageIndex;
                      return (
                        <tr key={`expense-view-${index}`}>
                          <td>{index + 1}</td>
                          <td>{entry.spent_on === "self" ? renderStatusBadge("Self", "info") : renderStatusBadge("Candidate", "secondary")}</td>
                          <td>{entry.purpose || "-"}</td>
                          <td>{entry.expense_date ? formatDateOnly(entry.expense_date) : "-"}</td>
                          <td>{entry.candidate_name || "-"}</td>
                          <td>{entry.mobile_number || "-"}</td>
                          <td>{entry.aadhaar_number || "-"}</td>
                          <td className="text-end" style={{ fontWeight: 600 }}>{formatAmount(entry.amount_spent || 0)}</td>
                          <td style={{ maxWidth: 240, whiteSpace: "normal" }}>{entry.reason || "-"}</td>
                        </tr>
                      );
                    }) : (
                      <tr><td colSpan="9" className="text-center py-3 text-muted">No expense entries.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {renderExpensesPagination(currentPage, totalEntries, updateExpensesViewModalPage)}
                </>
              )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {documentsModal.open && documentsModal.request && (
        <div className="advance-payment-modal-backdrop">
          <div
            className="advance-payment-modal"
            role="dialog"
            aria-modal="true"
            style={{ maxWidth: 960, width: "95%", maxHeight: "92vh", display: "flex", flexDirection: "column" }}
          >
            <div className="advance-payment-modal-header advance-payment-doc-modal-header">
              <div>
                <h3>
                  <i className={documentsModal.canUpload ? "ri-upload-cloud-2-line" : "ri-folder-open-line"} />
                  {documentsModal.canUpload ? "Upload Documents" : "View Documents"}
                </h3>
                <p className="advance-payment-modal-subtitle">
                  {documentsModal.request.request_code || "-"} · {documentsModal.request.client_name || "-"} | {formatAmount(documentsModal.request.amount)}
                </p>
              </div>
              <button type="button" onClick={closeDocumentsModal} aria-label="Close">
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="advance-payment-modal-body" style={{ overflowY: "auto" }}>
              {documentsModal.canUpload ? (
                <div
                  className={`advance-payment-doc-dropzone${docDragOver ? " is-dragover" : ""}`}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setDocDragOver(true);
                  }}
                  onDragLeave={() => setDocDragOver(false)}
                  onDrop={(event) => {
                    event.preventDefault();
                    setDocDragOver(false);
                    handleDocumentsUpload(event.dataTransfer.files);
                  }}
                >
                  <div className="advance-payment-doc-dropzone-icon">
                    <i className="ri-upload-cloud-2-line" />
                  </div>
                  <div className="advance-payment-doc-dropzone-title">Drag & drop files here</div>
                  <div className="advance-payment-doc-dropzone-hint">
                    PDF, JPG, PNG, DOC — up to 10MB each, multiple files allowed
                  </div>
                  <span className="advance-payment-doc-dropzone-browse">Browse Files</span>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                    disabled={documentsModal.uploading}
                    onChange={(event) => {
                      const files = event.target.files;
                      handleDocumentsUpload(files);
                      event.target.value = "";
                    }}
                  />
                  {documentsModal.uploading ? (
                    <div className="advance-payment-doc-uploading">
                      <span className="advance-payment-doc-spinner" /> Uploading...
                    </div>
                  ) : null}
                  {documentsModal.error ? (
                    <div className="advance-payment-error-text mt-2">{documentsModal.error}</div>
                  ) : null}
                </div>
              ) : null}

              <div className="advance-payment-doc-section-title">
                <span>Uploaded Documents</span>
                {documentsModal.documents.length > 0 ? (
                  <span className="advance-payment-doc-count-pill">{documentsModal.documents.length}</span>
                ) : null}
              </div>

              {documentsModal.documents.length === 0 ? (
                <div className="advance-payment-doc-empty">
                  <i className="ri-file-search-line" />
                  <span>No documents uploaded yet.</span>
                </div>
              ) : (
                <div className="advance-payment-doc-grid">
                  {documentsModal.documents.map((doc, docIndex) => {
                    const iconType = getDocIconType(doc);
                    return (
                      <div className="advance-payment-doc-card" key={`doc-${docIndex}`}>
                        <div className="advance-payment-doc-thumb">
                          {isImageDocument(doc) ? (
                            <img src={doc.url} alt={doc.original_name || "Document"} loading="lazy" />
                          ) : (
                            <div className={`advance-payment-doc-thumb-icon ${iconType}`}>
                              <i className={getDocIcon(iconType)} />
                            </div>
                          )}
                        </div>
                        <div className="advance-payment-doc-body">
                          <div className="advance-payment-doc-name" title={doc.original_name || "Document"}>
                            {doc.original_name || "Document"}
                          </div>
                          <div className="advance-payment-doc-meta">
                            {formatDateTime(doc.uploaded_at)} · {formatFileSize(doc.size)}
                          </div>
                          <div className="advance-payment-doc-actor">
                            <i className="ri-user-3-line" /> {doc.uploaded_by_name || "-"}
                          </div>
                          <div className="advance-payment-doc-actions">
                            {isPreviewableDocument(doc) ? (
                              <button
                                type="button"
                                className="advance-payment-doc-action-btn"
                                onClick={() => setDocPreview({
                                  url: buildDocPreviewUrl(doc),
                                  name: doc.original_name || "Document",
                                  isImage: isImageDocument(doc),
                                })}
                              >
                                <i className="ri-eye-line" /> Preview
                              </button>
                            ) : null}
                            <a
                              href={doc.url}
                              download={doc.original_name || undefined}
                              target="_blank"
                              rel="noreferrer"
                              className="advance-payment-doc-action-btn download"
                            >
                              <i className="ri-download-2-line" /> Download
                            </a>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="advance-payment-modal-footer">
              <button type="button" className="btn btn-light" onClick={closeDocumentsModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {docPreview ? (
        <div className="advance-payment-doc-preview-overlay" onClick={() => setDocPreview(null)}>
          <div className="advance-payment-doc-preview-box" onClick={(event) => event.stopPropagation()}>
            <div className="advance-payment-doc-preview-head">
              <span>{docPreview.name}</span>
              <button type="button" onClick={() => setDocPreview(null)} aria-label="Close preview">
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="advance-payment-doc-preview-body">
              {docPreview.isImage ? (
                <img src={docPreview.url} alt={docPreview.name} />
              ) : (
                <iframe src={docPreview.url} title={docPreview.name} />
              )}
            </div>
          </div>
        </div>
      ) : null}

      {expensesUploadModal.open && expensesUploadModal.request && (
        <div className="advance-payment-modal-backdrop">
          <div className="advance-payment-modal advance-payment-upload-modal" role="dialog" aria-modal="true">
            <div className="advance-payment-modal-header">
              <div>
                <h3>Bulk Upload Expenses</h3>
                <p className="advance-payment-modal-subtitle">
                  Upload expense rows in CSV format for this paid advance request.
                </p>
              </div>
              <button type="button" onClick={closeExpensesUploadModal} aria-label="Close">
                <i className="ri-close-line" />
              </button>
            </div>

            <form onSubmit={handleExpensesBulkUpload} noValidate>
              <div className="advance-payment-modal-body">
                <div className="advance-payment-decision-hero success">
                  <div className="advance-payment-decision-hero-icon">
                    <i className="ri-upload-cloud-2-line" />
                  </div>
                  <div className="advance-payment-decision-hero-copy">
                    <h4>{expensesUploadModal.request.request_code || "-"}</h4>
                    <p>
                      {expensesUploadModal.request.client_name || "-"} | {formatAmount(expensesUploadModal.request.amount)}
                    </p>
                  </div>
                </div>

                <div className="advance-payment-upload-panel">
                  <div className="advance-payment-upload-actions">
                    <button
                      type="button"
                      className="advance-payment-secondary-btn"
                      onClick={handleDownloadExpenseDemoCsv}
                    >
                      <i className="ri-download-2-line" />
                      Download Demo CSV
                    </button>
                  </div>

                  <div className="advance-payment-upload-note">
                    Use this header order: <strong>spent_on, purpose, expense_date, candidate_name, mobile_number, aadhaar_number, amount_spent, reason</strong>
                    <br />
                    <strong>spent_on</strong> must be <strong>self</strong> or <strong>candidate</strong>. For <strong>self</strong> rows, candidate_name and mobile_number can be left blank — they are filled from your own profile automatically.
                  </div>

                  <div className="mt-3">
                    <label className="advance-payment-form-label">Upload CSV File</label>
                    <input
                      type="file"
                      className="form-control"
                      accept=".csv,text/csv"
                      onChange={handleExpensesUploadFileChange}
                    />
                    {expensesUploadModal.fileName ? (
                      <div className="advance-payment-upload-file-name">
                        Selected file: <strong>{expensesUploadModal.fileName}</strong>
                      </div>
                    ) : null}
                    {expensesUploadModal.generalError ? (
                      <div className="advance-payment-error-text">{expensesUploadModal.generalError}</div>
                    ) : null}
                  </div>

                  {expensesUploadModal.errors.length > 0 ? (
                    <div className="advance-payment-upload-errors">
                      <div className="advance-payment-panel-title">Validation Errors</div>
                      <ul>
                        {expensesUploadModal.errors.map((errorText, index) => (
                          <li key={`expense-upload-error-${index}`}>{errorText}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="advance-payment-modal-footer">
                <button type="button" className="btn btn-light" onClick={closeExpensesUploadModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="advance-payment-primary-btn"
                  disabled={busyByRequest[expensesUploadModal.request.id] === "bulk_upload_expenses"}
                >
                  <i className="ri-upload-cloud-2-line" />
                  {busyByRequest[expensesUploadModal.request.id] === "bulk_upload_expenses" ? "Uploading..." : "Upload CSV"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {recoveryModal.open && recoveryModal.request && (
        <div className="advance-payment-modal-backdrop">
          <div className="advance-payment-modal advance-payment-expenses-modal" role="dialog" aria-modal="true">
            <div className="advance-payment-modal-header">
              <div>
                <h3>Add Recovery Entry</h3>
                <p className="advance-payment-modal-subtitle">
                  Add estimated, actual, or non-recovery amounts. Totals stay cumulative and cannot exceed total spent.
                </p>
              </div>
              <button type="button" onClick={closeRecoveryModal} aria-label="Close">
                <i className="ri-close-line" />
              </button>
            </div>

            <form onSubmit={handleRecoverySubmit} noValidate>
              <div className="advance-payment-modal-body">
                <div className="advance-payment-summary-stats advance-payment-summary-stats-spaced advance-payment-summary-stats--compact-row">
                  <div className="advance-payment-summary-stat">
                    <span className="advance-payment-summary-label">Amount Received</span>
                    <strong>{formatAmount(recoveryModal.request.amount)}</strong>
                  </div>
                  <div className="advance-payment-summary-stat">
                    <span className="advance-payment-summary-label">Total Spent</span>
                    <strong>{formatAmount(recoveryModal.request.expenses_summary?.total_spent || 0)}</strong>
                  </div>
                  <div className="advance-payment-summary-stat">
                    <span className="advance-payment-summary-label">Leftover Available</span>
                    <strong>{formatAmount(recoveryModal.request.leftover_info?.available ?? recoveryModal.request.recovery_summary?.leftover_amount ?? 0)}</strong>
                  </div>
                  {Number(recoveryModal.request.leftover_info?.consumed || 0) > 0 ? (
                    <div className="advance-payment-summary-stat">
                      <span className="advance-payment-summary-label" style={{ color: "#0e479c" }}>Reused Out</span>
                      <strong style={{ color: "#0e479c" }}>{formatAmount(recoveryModal.request.leftover_info.consumed)}</strong>
                    </div>
                  ) : null}
                  <div className="advance-payment-summary-stat">
                    <span className="advance-payment-summary-label">Recoverable Amount</span>
                    <strong>{formatAmount(recoveryModal.request.recovery_summary?.recoverable_amount || 0)}</strong>
                  </div>
                  <div className="advance-payment-summary-stat">
                    <span className="advance-payment-summary-label">Non-Recoverable</span>
                    <strong>{formatAmount(recoveryModal.request.recovery_summary?.non_recovery_amount || 0)}</strong>
                  </div>
                </div>

                <div className="advance-payment-detail-grid advance-payment-recovery-grid">
                  <div className="advance-payment-detail-panel">
                    <div className="advance-payment-panel-title">Recovery Estimate</div>
                    <div className="advance-payment-recovery-fields">
                      <div className="advance-payment-field-block">
                        <label className="advance-payment-form-label">Estimated Amount</label>
                        <input
                          name="estimatedRecoveryAmount"
                          type="text"
                          inputMode="decimal"
                          className="form-control"
                          placeholder="Enter estimated amount"
                          value={recoveryModal.form.estimatedRecoveryAmount}
                          onChange={handleRecoveryFieldChange}
                        />
                        {recoveryModal.errors.estimatedRecoveryAmount ? <div className="advance-payment-error-text">{recoveryModal.errors.estimatedRecoveryAmount}</div> : null}
                      </div>
                      <div className="advance-payment-field-block">
                        <label className="advance-payment-form-label">Estimated Recovery Date</label>
                        <input
                          name="estimatedRecoveryDate"
                          type="date"
                          className="form-control"
                          value={recoveryModal.form.estimatedRecoveryDate}
                          onChange={handleRecoveryFieldChange}
                        />
                        {recoveryModal.errors.estimatedRecoveryDate ? <div className="advance-payment-error-text">{recoveryModal.errors.estimatedRecoveryDate}</div> : null}
                      </div>
                    </div>
                  </div>

                  <div className="advance-payment-detail-panel">
                    <div className="advance-payment-panel-title">Actual Recovery</div>
                    <div className="advance-payment-recovery-fields">
                      <div className="advance-payment-field-block">
                        <label className="advance-payment-form-label">Actual Recovery Amount</label>
                        <input
                          name="actualRecoveryAmount"
                          type="text"
                          inputMode="decimal"
                          className="form-control"
                          placeholder="Enter actual recovered amount"
                          value={recoveryModal.form.actualRecoveryAmount}
                          onChange={handleRecoveryFieldChange}
                        />
                        {recoveryModal.errors.actualRecoveryAmount ? <div className="advance-payment-error-text">{recoveryModal.errors.actualRecoveryAmount}</div> : null}
                      </div>
                      <div className="advance-payment-field-block">
                        <label className="advance-payment-form-label">Actual Recovery Date</label>
                        <input
                          name="actualRecoveryDate"
                          type="date"
                          className="form-control"
                          value={recoveryModal.form.actualRecoveryDate}
                          onChange={handleRecoveryFieldChange}
                        />
                        {recoveryModal.errors.actualRecoveryDate ? <div className="advance-payment-error-text">{recoveryModal.errors.actualRecoveryDate}</div> : null}
                      </div>
                    </div>
                  </div>

                  <div className="advance-payment-detail-panel">
                    <div className="advance-payment-panel-title">Non Recovery</div>
                    <div className="advance-payment-recovery-fields">
                      <div className="advance-payment-field-block">
                        <label className="advance-payment-form-label">Non Recovery Amount</label>
                        <input
                          name="nonRecoveryAmount"
                          type="text"
                          inputMode="decimal"
                          className="form-control"
                          placeholder="Enter non recovery amount"
                          value={recoveryModal.form.nonRecoveryAmount}
                          onChange={handleRecoveryFieldChange}
                        />
                        {recoveryModal.errors.nonRecoveryAmount ? <div className="advance-payment-error-text">{recoveryModal.errors.nonRecoveryAmount}</div> : null}
                      </div>
                      <div className="advance-payment-field-block">
                        <label className="advance-payment-form-label">Non Recovery Reason</label>
                        <input
                          name="nonRecoveryReason"
                          type="text"
                          className="form-control"
                          placeholder="Enter reason"
                          value={recoveryModal.form.nonRecoveryReason}
                          onChange={handleRecoveryFieldChange}
                        />
                        {recoveryModal.errors.nonRecoveryReason ? <div className="advance-payment-error-text">{recoveryModal.errors.nonRecoveryReason}</div> : null}
                      </div>
                    </div>
                  </div>

                  <div className="advance-payment-detail-panel advance-payment-recovery-remark-panel">
                    <div className="advance-payment-panel-title">Remark</div>
                    <textarea
                      name="recoveryRemark"
                      className="form-control advance-payment-textarea"
                      rows="3"
                      placeholder="Add entry remark"
                      value={recoveryModal.form.recoveryRemark}
                      onChange={handleRecoveryFieldChange}
                    />
                    {recoveryModal.errors.form ? <div className="advance-payment-error-text">{recoveryModal.errors.form}</div> : null}
                  </div>
                </div>
              </div>

              <div className="advance-payment-modal-footer">
                <button type="button" className="btn btn-light" onClick={closeRecoveryModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="advance-payment-primary-btn"
                  disabled={busyByRequest[recoveryModal.request.id] === "update_recovery"}
                >
                  <i className="ri-check-line" />
                  {busyByRequest[recoveryModal.request.id] === "update_recovery" ? "Saving..." : "Add Recovery Entry"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {recoveryViewModal.open && recoveryViewModal.request && (
        <div className="advance-payment-modal-backdrop">
          <div className="advance-payment-modal advance-payment-expenses-modal" role="dialog" aria-modal="true">
            <div className="advance-payment-modal-header">
              <div>
                <h3>Recovery Overview</h3>
                <p className="advance-payment-modal-subtitle">
                  Review cumulative totals and the full recovery history for this paid request.
                </p>
              </div>
              <button type="button" onClick={closeRecoveryViewModal} aria-label="Close">
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="advance-payment-modal-body">
              <div className="advance-payment-summary-stats advance-payment-summary-stats-spaced advance-payment-summary-stats--compact-row">
                <div className="advance-payment-summary-stat">
                  <span className="advance-payment-summary-label">Amount Received</span>
                  <strong>{formatAmount(recoveryViewModal.request.amount)}</strong>
                </div>
                <div className="advance-payment-summary-stat">
                  <span className="advance-payment-summary-label">Total Spent</span>
                  <strong>{formatAmount(recoveryViewModal.request.expenses_summary?.total_spent || 0)}</strong>
                </div>
                <div className="advance-payment-summary-stat">
                  <span className="advance-payment-summary-label">Leftover Available</span>
                  <strong>{formatAmount(recoveryViewModal.request.leftover_info?.available ?? recoveryViewModal.request.recovery_summary?.leftover_amount ?? 0)}</strong>
                </div>
                {Number(recoveryViewModal.request.leftover_info?.consumed || 0) > 0 ? (
                  <div className="advance-payment-summary-stat">
                    <span className="advance-payment-summary-label" style={{ color: "#0e479c" }}>Reused Out</span>
                    <strong style={{ color: "#0e479c" }}>{formatAmount(recoveryViewModal.request.leftover_info.consumed)}</strong>
                  </div>
                ) : null}
                <div className="advance-payment-summary-stat">
                  <span className="advance-payment-summary-label">Recoverable Amount</span>
                  <strong>{formatAmount(recoveryViewModal.request.recovery_summary?.recoverable_amount || 0)}</strong>
                </div>
                <div className="advance-payment-summary-stat">
                  <span className="advance-payment-summary-label">Non-Recoverable</span>
                  <strong>{formatAmount(recoveryViewModal.request.recovery_summary?.non_recovery_amount || 0)}</strong>
                </div>
              </div>

              <div className="advance-payment-detail-grid advance-payment-recovery-view-grid">
                <div className="advance-payment-detail-panel advance-payment-recovery-view-panel advance-payment-recovery-view-panel--full">
                  <div className="advance-payment-panel-title">Recovery Totals</div>
                  <div className="advance-payment-detail-list advance-payment-detail-list-inline advance-payment-detail-list-inline-compact">
                    <div><strong>Estimated Total :</strong> {formatAmount(recoveryViewModal.request.recovery_summary?.estimated_amount || 0)}</div>
                    <div><strong>Actual Total :</strong> {formatAmount(recoveryViewModal.request.recovery_summary?.actual_amount || 0)}</div>
                    <div><strong>Recovery Entries :</strong> {recoveryViewModal.request.recovery_summary?.history_count || 0}</div>
                  </div>
                </div>
              </div>

              <div className="advance-payment-detail-panel advance-payment-recovery-view-panel mt-3">
                <div className="advance-payment-panel-title">Recovery History</div>
                {(Array.isArray(recoveryViewModal.request.recovery_history) && recoveryViewModal.request.recovery_history.length > 0)
                || (Array.isArray(recoveryViewModal.request.reuse_consumers) && recoveryViewModal.request.reuse_consumers.length > 0)
                || (Array.isArray(recoveryViewModal.request.expense_recovery_history) && recoveryViewModal.request.expense_recovery_history.length > 0) ? (
                  <div className="advance-payment-recovery-history-list">
                    {Array.isArray(recoveryViewModal.request.expense_recovery_history) && recoveryViewModal.request.expense_recovery_history.map((entry, index) => (
                      <div className="advance-payment-recovery-history-card" key={`expense-recovery-${index}`} style={{ background: "#effcf3", borderColor: "#bdeecd" }}>
                        <div className="advance-payment-recovery-history-head">
                          <div>
                            <strong style={{ color: "#0f766e" }}>
                              <i className="ri-checkbox-circle-line me-1" /> Recovered by Accounts
                            </strong>
                            <span>{formatDateTime(entry.marked_at)}</span>
                          </div>
                          <span className="advance-payment-recovery-history-actor">
                            {entry.marked_by_name || "-"}
                          </span>
                        </div>

                        <div className="advance-payment-detail-list advance-payment-detail-list-inline">
                          <div>
                            <strong>Total Recovered:</strong>
                            {formatAmount(entry.total_amount)}
                          </div>
                          {Array.isArray(entry.items) && entry.items.length > 0 ? (
                            <div>
                              <strong>Rows:</strong>
                              {entry.items.map((item) => `${item.purpose || item.candidate_name || "Row"} (${formatAmount(item.recovered_amount)})`).join(", ")}
                            </div>
                          ) : null}
                          {entry.remark ? (
                            <div>
                              <strong>Remark:</strong>
                              {entry.remark}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ))}
                    {Array.isArray(recoveryViewModal.request.reuse_consumers) && recoveryViewModal.request.reuse_consumers.map((consumer, index) => (
                      <div className="advance-payment-recovery-history-card" key={`reuse-consumer-${index}`} style={{ background: "#f5f8fc", borderColor: "#cfe2ff" }}>
                        <div className="advance-payment-recovery-history-head">
                          <div>
                            <strong style={{ color: "#0e479c" }}>
                              <i className="ri-recycle-line me-1" /> Reused Out
                            </strong>
                            <span>{formatDateTime(consumer.created_at)}</span>
                          </div>
                          <span className="advance-payment-recovery-history-actor">
                            {consumer.requester_name || "-"}
                          </span>
                        </div>

                        <div className="advance-payment-detail-list advance-payment-detail-list-inline">
                          <div>
                            <strong>Amount:</strong>
                            {formatAmount(consumer.amount)}
                          </div>
                          <div>
                            <strong>Reused Into:</strong>
                            {consumer.request_code}
                          </div>
                        </div>
                      </div>
                    ))}
                    {Array.isArray(recoveryViewModal.request.recovery_history) && recoveryViewModal.request.recovery_history.map((entry, index) => (
                      <div className="advance-payment-recovery-history-card" key={`recovery-history-${index}`}>
                        <div className="advance-payment-recovery-history-head">
                          <div>
                            <strong>Recovery {index + 1}</strong>
                            <span>{formatDateTime(entry.created_at)}</span>
                          </div>
                          <span className="advance-payment-recovery-history-actor">
                            {entry.created_by_name || "-"}
                          </span>
                        </div>

                        <div className="advance-payment-detail-list advance-payment-detail-list-inline">
                          {Number(entry.estimated_recovery_amount || 0) > 0 ? (
                            <div>
                              <strong>Estimated:</strong>
                              {formatAmount(entry.estimated_recovery_amount)}
                              {entry.estimated_recovery_date ? ` on ${formatDateOnly(entry.estimated_recovery_date)}` : ""}
                            </div>
                          ) : null}

                          {Number(entry.actual_recovery_amount || 0) > 0 ? (
                            <div>
                              <strong>Actual Recovery:</strong>
                              {formatAmount(entry.actual_recovery_amount)}
                              {entry.actual_recovery_date ? ` on ${formatDateOnly(entry.actual_recovery_date)}` : ""}
                            </div>
                          ) : null}

                          {Number(entry.non_recovery_amount || 0) > 0 ? (
                            <div>
                              <strong>Non-Recoverable:</strong>
                              {formatAmount(entry.non_recovery_amount)}
                            </div>
                          ) : null}

                          {entry.non_recovery_reason ? (
                            <div>
                              <strong>Reason:</strong>
                              {entry.non_recovery_reason}
                            </div>
                          ) : null}

                          {entry.recovery_remark ? (
                            <div>
                              <strong>Remark:</strong>
                              {entry.recovery_remark}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="advance-payment-muted-text">No recovery entries added yet.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {markRecoveredModal.open && markRecoveredModal.request && (
        <div className="advance-payment-modal-backdrop">
          <div className="advance-payment-modal advance-payment-expenses-modal" role="dialog" aria-modal="true" style={{ maxWidth: 1500, width: "97%" }}>
            <div className="advance-payment-modal-header">
              <div>
                <h3>Mark Recovered</h3>
                <p className="advance-payment-modal-subtitle">
                  {markRecoveredModal.request.request_code || "-"} · {markRecoveredModal.request.client_name || "-"} | {formatAmount(markRecoveredModal.request.amount)}
                </p>
              </div>
              <button type="button" onClick={closeMarkRecoveredModal} aria-label="Close">
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="advance-payment-modal-body">
              {markRecoveredModal.loading ? (
                <div className="advance-payment-loading-block">Loading expense rows...</div>
              ) : (
                <>
                  <div className="table-responsive" style={{ overflowX: "auto" }}>
                    <table className="table table-bordered advance-payment-recovery-mark-table mb-0" style={{ minWidth: 1300 }}>
                      <thead>
                        <tr>
                          <th style={{ width: 50 }}>S.No</th>
                          <th>Spent On</th>
                          <th>Purpose</th>
                          <th>Date</th>
                          <th>Candidate</th>
                          <th>Mobile</th>
                          <th>Aadhaar</th>
                          <th>Reason</th>
                          <th className="text-end">Amount Spent</th>
                          <th className="text-end">Already Recovered</th>
                          <th style={{ width: 170 }}>Recovered Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {markRecoveredModal.entries.length > 0 ? markRecoveredModal.entries.map((entry, rowIndex) => {
                          const entryId = entry.entry_id || "";
                          const rowKey = entryId || `noid-${markRecoveredModal.page}-${rowIndex}`;
                          const amountSpent = Number(entry.amount_spent || 0);
                          const alreadyRecovered = Number(entry.already_recovered || 0);
                          const remaining = Math.max(0, amountSpent - alreadyRecovered);
                          const value = entryId ? (markRecoveredModal.values[entryId] ?? "") : "";
                          const rowError = entryId ? markRecoveredModal.rowErrors[entryId] : "";
                          const isFullyRecovered = remaining <= 0 && amountSpent > 0;
                          const serialNumber = ((Number(markRecoveredModal.page || 1) - 1) * EXPENSES_PAGE_SIZE) + rowIndex + 1;
                          return (
                            <tr key={`mark-recovered-${rowKey}`}>
                              <td>{serialNumber}</td>
                              <td>
                                {entry.spent_on === "self" ? renderStatusBadge("Self", "info") : renderStatusBadge("Candidate", "secondary")}
                              </td>
                              <td>{entry.purpose || "-"}</td>
                              <td>{entry.expense_date ? formatDateOnly(entry.expense_date) : "-"}</td>
                              <td>{entry.candidate_name || "-"}</td>
                              <td className="advance-payment-recovery-mark-mono">{entry.mobile_number || "-"}</td>
                              <td className="advance-payment-recovery-mark-mono">{entry.aadhaar_number || "-"}</td>
                              <td className="advance-payment-recovery-mark-reason">{entry.reason || "-"}</td>
                              <td className="text-end" style={{ fontWeight: 600 }}>{formatAmount(amountSpent)}</td>
                              <td className="text-end">{formatAmount(alreadyRecovered)}</td>
                              <td>
                                {!entryId ? (
                                  <span className="advance-payment-muted-text" title="Open Update Expenses and hit Save once (no changes needed) to enable recovery marking for this row.">
                                    Save expenses once first
                                  </span>
                                ) : isFullyRecovered ? (
                                  <div className="advance-payment-recovery-mark-fully">
                                    <i className="ri-checkbox-circle-fill" /> Fully recovered
                                  </div>
                                ) : (
                                  <>
                                    <div className={`advance-payment-recovery-mark-input${rowError ? " has-error" : ""}`}>
                                      <span>₹</span>
                                      <input
                                        type="text"
                                        inputMode="decimal"
                                        placeholder="0.00"
                                        value={value}
                                        onChange={(event) => {
                                          const nextValue = formatAmountInput(event.target.value);
                                          handleMarkRecoveredValueChange(entryId, nextValue, remaining);
                                        }}
                                      />
                                    </div>
                                    {rowError ? (
                                      <div className="advance-payment-error-text">{rowError}</div>
                                    ) : null}
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        }) : (
                          <tr><td colSpan="11" className="text-center py-3 text-muted">No expense entries for this request.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {renderExpensesPagination(markRecoveredModal.page, markRecoveredModal.totalEntries, updateMarkRecoveredPage)}

                  <div className="mt-3">
                    <label className="advance-payment-form-label">Remark (optional)</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      placeholder="Add a remark for this recovery update..."
                      value={markRecoveredModal.remark}
                      onChange={(event) => setMarkRecoveredModal((prev) => ({ ...prev, remark: event.target.value }))}
                    />
                  </div>

                  {markRecoveredModal.error ? (
                    <div className="advance-payment-error-text mt-2">{markRecoveredModal.error}</div>
                  ) : null}
                </>
              )}
            </div>

            <div className="advance-payment-modal-footer">
              <button type="button" className="btn btn-light" onClick={closeMarkRecoveredModal}>
                Cancel
              </button>
              <button
                type="button"
                className="advance-payment-primary-btn"
                onClick={handleMarkRecoveredSubmit}
                disabled={markRecoveredModal.submitting || markRecoveredModal.loading}
              >
                {markRecoveredModal.submitting ? "Saving..." : "Mark Recovered"}
              </button>
            </div>
          </div>
        </div>
      )}

      {accountsModal.open && accountsModal.request && (
        <div className="advance-payment-modal-backdrop">
          <div className="advance-payment-modal advance-payment-decision-modal" role="dialog" aria-modal="true" style={{ maxWidth: 820, width: "92%" }}>
            <div className="advance-payment-modal-header">
              <div>
                <h3>Update Details</h3>
                <p className="advance-payment-modal-subtitle">
                  Save UTR details and optionally mark the request as paid.
                </p>
              </div>
              <button type="button" onClick={closeAccountsModal} aria-label="Close">
                <i className="ri-close-line" />
              </button>
            </div>

            <form onSubmit={handleAccountsModalSubmit} noValidate>
              <div className="advance-payment-modal-body">
                <div className="advance-payment-decision-hero success">
                  <div className="advance-payment-decision-hero-icon">
                    <i className="ri-secure-payment-line" />
                  </div>
                  <div className="advance-payment-decision-hero-copy">
                    <h4>{accountsModal.request.request_code || "-"}</h4>
                    <p>
                      {accountsModal.request.client_name || "-"} | {formatAmount(accountsModal.request.amount)}
                    </p>
                  </div>
                </div>

                <div className="advance-payment-detail-grid single-column">
                  {accountsModal.request.bank_snapshot && accountsModal.request.bank_snapshot.account_no ? (
                    <div className="advance-payment-detail-panel" style={{ background: "#f5f8fc", borderColor: "#cfe2ff" }}>
                      <div className="advance-payment-panel-title">
                        <i className="ri-bank-line me-1" /> Requester's Bank Details
                      </div>
                      <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.8 }}>
                        <div><strong>Holder:</strong> {accountsModal.request.bank_snapshot.holder_name || "-"}</div>
                        <div>
                          <strong>A/c:</strong>{" "}
                          <span style={{ fontFamily: "monospace" }}>{(accountsModal.request.bank_snapshot.account_no || "").replace(/(.{4})/g, "$1 ").trim()}</span>
                          <button
                            type="button"
                            className="btn btn-sm ms-2"
                            style={{
                              padding: "0 8px",
                              fontSize: 11,
                              background: copiedKey === "acct" ? "#dcfce7" : "#f1f3f6",
                              color: copiedKey === "acct" ? "#15803d" : "#0e479c",
                              transition: "background .15s",
                            }}
                            onClick={() => copyToClipboard(accountsModal.request.bank_snapshot.account_no, "acct")}
                            title="Copy account number"
                          >
                            <i className={copiedKey === "acct" ? "ri-check-line" : "ri-file-copy-line"} />
                            {copiedKey === "acct" ? <span style={{ marginLeft: 4 }}>Copied</span> : null}
                          </button>
                        </div>
                        <div>
                          <strong>IFSC:</strong>{" "}
                          <span style={{ fontFamily: "monospace" }}>{accountsModal.request.bank_snapshot.ifsc}</span>
                          <button
                            type="button"
                            className="btn btn-sm ms-2"
                            style={{
                              padding: "0 8px",
                              fontSize: 11,
                              background: copiedKey === "ifsc" ? "#dcfce7" : "#f1f3f6",
                              color: copiedKey === "ifsc" ? "#15803d" : "#0e479c",
                              transition: "background .15s",
                            }}
                            onClick={() => copyToClipboard(accountsModal.request.bank_snapshot.ifsc, "ifsc")}
                            title="Copy IFSC"
                          >
                            <i className={copiedKey === "ifsc" ? "ri-check-line" : "ri-file-copy-line"} />
                            {copiedKey === "ifsc" ? <span style={{ marginLeft: 4 }}>Copied</span> : null}
                          </button>
                        </div>
                        <div>
                          <strong>Bank:</strong> {accountsModal.request.bank_snapshot.bank_name || "-"}
                          {accountsModal.request.bank_snapshot.branch_name ? <> — {accountsModal.request.bank_snapshot.branch_name}</> : null}
                        </div>
                        {accountsModal.request.bank_snapshot.verified_at ? (
                          <div style={{ marginTop: 4 }}>
                            <span className="badge bg-success-transparent">
                              <i className="ri-shield-check-line me-1" /> Verified via Surepass
                            </span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : null}

                  {accountsModal.request.reuse_info?.is_reuse ? (
                    <div className="advance-payment-detail-panel" style={{ background: "#f0f7ff", borderColor: "#cfe2ff" }}>
                      <div className="advance-payment-panel-title">
                        <i className="ri-recycle-line me-1" /> Reused from Previous Request
                      </div>
                      <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.8 }}>
                        <div><strong>Source:</strong> {accountsModal.request.reuse_info.reused_from_request_code}</div>
                        <div><strong>Reused Amount:</strong> {formatAmount(accountsModal.request.reuse_info.reused_amount)}</div>
                        <div><strong>Fresh Ask (to transfer):</strong> <span style={{ color: "#0e479c", fontSize: 15, fontWeight: 700 }}>{formatAmount(accountsModal.request.reuse_info.effective_ask_amount)}</span></div>
                        {Number(accountsModal.request.reuse_info.effective_ask_amount) <= 0 ? (
                          <div className="mt-2" style={{ background: "#fef3c7", border: "1px solid #fde68a", padding: "8px 10px", borderRadius: 6, color: "#92400e", fontSize: 12 }}>
                            <i className="ri-information-fill me-1" />
                            Fully covered by reuse — no fresh transfer needed. Leave UTR blank to auto-generate <code>REUSE-INTERNAL-{accountsModal.request.reuse_info.reused_from_request_code}</code>.
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : null}

                  <div className="advance-payment-detail-panel">
                    <div className="advance-payment-panel-title">UTR Details</div>
                    <label className="advance-payment-form-label" htmlFor="advanceAccountsUtr">
                      UTR Number
                    </label>
                    <input
                      id="advanceAccountsUtr"
                      type="text"
                      className="form-control"
                      placeholder="Enter UTR number"
                      value={accountsModal.utrNo}
                      onChange={(event) => setAccountsModal((prev) => ({ ...prev, utrNo: event.target.value }))}
                    />

                    <label className="advance-payment-form-label mt-3" htmlFor="advanceAccountsRemark">
                      Remark
                    </label>
                    <input
                      id="advanceAccountsRemark"
                      type="text"
                      className="form-control"
                      placeholder="Add remark"
                      value={accountsModal.remark}
                      onChange={(event) => setAccountsModal((prev) => ({ ...prev, remark: event.target.value }))}
                    />
                  </div>

                  <div className="advance-payment-detail-panel">
                    <div className="advance-payment-switch-row">
                      <div>
                        <div className="advance-payment-panel-title mb-0">Mark Paid</div>
                        <div className="advance-payment-muted-text">Turn this on to complete the request after saving UTR.</div>
                      </div>
                      <label className="advance-payment-switch">
                        <input
                          type="checkbox"
                          checked={accountsModal.markPaid}
                          onChange={(event) => setAccountsModal((prev) => ({ ...prev, markPaid: event.target.checked }))}
                        />
                        <span className="advance-payment-switch-slider" />
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="advance-payment-modal-footer">
                <button type="button" className="btn btn-light" onClick={closeAccountsModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="advance-payment-primary-btn"
                  disabled={busyByRequest[accountsModal.request.id] === "save_utr" || busyByRequest[accountsModal.request.id] === "mark_paid"}
                >
                  <i className="ri-check-line" />
                  {busyByRequest[accountsModal.request.id] === "save_utr" || busyByRequest[accountsModal.request.id] === "mark_paid"
                    ? "Saving..."
                    : accountsModal.markPaid
                      ? "Save and Mark Paid"
                      : "Save UTR"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdvancePayment;
